// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component'
import { JobPoller, JobStatus } from '@umd-dit-ai-workspace/jobs-component'
import { documentsApi, analysisHistoryApi } from '@/api'
import { configApi } from '@/api/config'
import type { Document } from '@/api'
import type { AnalysisSummary } from '@/api/analysis-history'
import { LearningObjectivesSimplified, CLOHeatmap, PrerequisiteGapAnalysis, PrerequisiteReadiness, PLOHeatmap } from '@/components/AnalysisReport'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { exportToAccessiblePDF } from '@/utils/accessiblePdfExport'
import { format } from 'date-fns'
import { Eye, Download, X, Loader2, FileText, CheckCircle, XCircle, Clock, Trash2, File, Folder, FolderPlus, GripVertical, Pencil } from 'lucide-react'
import { formatInTimeZone } from 'date-fns-tz'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// ─── Constants ────────────────────────────────────────────────────────────────
const LO_PROMPT_NAME = 'learning_objective_prompt'
const LO_SCHEMA_NAME = 'learning_objective_schema'
const LO_ANALYSIS_TYPE = 'learning objectives'

// ─── Helpers ──────────────────────────────────────────────────────────────────
function extractObjectivesFromAnalysisData(analysisData: any): any[] {
  if (!analysisData) return []
  if (Array.isArray(analysisData.learning_objectives) && analysisData.learning_objectives.length > 0) {
    return analysisData.learning_objectives
  }
  return []
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function LearningObjectivesPage() {
  const [activeTab, setActiveTab] = useState<'history' | 'extract' | 'compare' | 'plo' | 'plo-mapping'>('extract')
  const [selectedFolder, setSelectedFolder] = useState<string>('all')
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth()

  const { data: documents = [] } = useQuery<Document[]>({
    queryKey: ['documents', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => documentsApi.getDocuments(selectedOrganizationId!, selectedWorkspaceId!),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
    refetchInterval: 5000,
  })

  const folders = useMemo(() => {
    const unique = new Set<string>()
    documents.forEach(doc => { if (doc.folder) unique.add(doc.folder) })
    return Array.from(unique).sort()
  }, [documents])

  const getFolderCount = (folder: string) =>
    documents.filter(doc => doc.folder === folder).length

  return (
    <div className="flex h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">

      {/* Folder sidebar — only on Extract tab */}
      {activeTab === 'extract' && (
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col shadow-lg">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">📁 Folders</h2>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            <button
              onClick={() => setSelectedFolder('all')}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors mb-1 ${
                selectedFolder === 'all'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <File className="w-5 h-5" />
              <span className="font-medium flex-1 text-left">All Documents</span>
              <span className="text-sm font-semibold">{documents.length}</span>
            </button>

            {folders.length > 0 && (
              <>
                <div className="pt-4 pb-2 px-3">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Courses</h3>
                </div>
                {folders.map(folder => (
                  <button
                    key={folder}
                    onClick={() => setSelectedFolder(folder)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors mb-1 ${
                      selectedFolder === folder
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Folder className="w-5 h-5" />
                    <span className="font-medium flex-1 text-left uppercase">{folder}</span>
                    <span className="text-sm font-semibold">{getFolderCount(folder)}</span>
                  </button>
                ))}
              </>
            )}

            {folders.length === 0 && (
              <div className="px-3 py-8 text-center">
                <FolderPlus className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No folders yet</p>
                <p className="text-xs text-gray-400 mt-1">Upload documents with folders to see them here</p>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-600">
              <div className="flex justify-between mb-1">
                <span>Total Folders:</span>
                <span className="font-semibold">{folders.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Documents:</span>
                <span className="font-semibold">{documents.length}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <div className="w-full px-6 py-8">

          {/* Header card */}
          <div className="bg-white border border-gray-200 p-8 rounded-xl shadow-sm mb-8">
            <h1 className="text-4xl font-bold mb-2 text-gray-900">Learning Outcomes</h1>
            <p className="text-lg text-gray-600">
              Extract and review learning outcomes from course documents
            </p>
          </div>

          {/* Tab nav */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                {([
                  { id: 'extract', label: 'Extract LO from Documents', tip: 'Extract learning objectives from your uploaded course documents (syllabi, exams, assignments)' },
                  { id: 'history', label: 'Bulk LO Analysis History', tip: 'Browse and reopen past bulk learning-objective extraction runs across multiple documents.' },
                  { id: 'compare', label: 'CLO Comparison', tip: 'Compare Course Learning Objectives across course instances as a coverage heatmap, or run a directional prerequisite readiness check.' },
                  { id: 'plo', label: 'PLO Generation', tip: 'Synthesize Program Learning Outcomes and view the curricular (I/R/M) map.' },
                  { id: 'plo-mapping', label: 'PLO Mapping (Custom)', tip: 'Map your bulk analysis comparisons against your own predefined PLOs.' },
                ] as const).map(tab => (
                  <div key={tab.id} className="relative group">
                    <button
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                    {/* Hover tooltip */}
                    <div
                      role="tooltip"
                      className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal leading-snug text-white opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100"
                    >
                      {tab.tip}
                    </div>
                  </div>
                ))}
              </nav>
            </div>
          </div>

          <div className={activeTab !== 'extract' ? 'hidden' : ''}><ExtractTab selectedFolder={selectedFolder} /></div>
          <div className={activeTab !== 'history' ? 'hidden' : ''}><LOHistoryTab /></div>
          <div className={activeTab !== 'compare' ? 'hidden' : ''}><CLOComparisonTab /></div>
          <div className={activeTab !== 'plo' ? 'hidden' : ''}><PLOTab /></div>
          <div className={activeTab !== 'plo-mapping' ? 'hidden' : ''}><PLOMappingTab /></div>

        </div>
      </div>
    </div>
  )
}

// ─── Pagination helpers ────────────────────────────────────────────────────────
function PaginationTop({ total, page, pageSize, label, onPageSizeChange }: {
  total: number; page: number; pageSize: number; label: string
  onPageSizeChange: (n: number) => void
}) {
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)
  return (
    <div className="flex justify-between items-center mb-4">
      <div className="text-sm text-gray-600">Showing {start}–{end} of {total} {label}</div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">Rows per page:</label>
        <select value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
    </div>
  )
}

function PaginationBottom({ total, page, pageSize, onPrev, onNext }: {
  total: number; page: number; pageSize: number
  onPrev: () => void; onNext: () => void
}) {
  const totalPages = Math.ceil(total / pageSize)
  if (totalPages <= 1) return null
  return (
    <div className="flex justify-between items-center pt-4 border-t mt-4">
      <div className="text-sm text-gray-600">Page {page} of {totalPages}</div>
      <div className="flex gap-2">
        <button onClick={onPrev} disabled={page === 1}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors">
          ← Previous
        </button>
        <button onClick={onNext} disabled={page === totalPages}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors">
          Next →
        </button>
      </div>
    </div>
  )
}

// ─── LO History Tab ───────────────────────────────────────────────────────────
function LOHistoryTab() {
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth()
  const [viewingAnalysisId, setViewingAnalysisId] = useState<string | null>(null)
  const [viewResult, setViewResult] = useState<{
    sourceLabel: string
    objectives: any[]
    documentCount?: number
  } | null>(null)
  const [viewLoading, setViewLoading] = useState(false)
  const [viewError, setViewError] = useState<string | null>(null)
  const [downloadingPdfFor, setDownloadingPdfFor] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [analysisToDelete, setAnalysisToDelete] = useState<string | null>(null)
  const [loSortCol, setLoSortCol] = useState<'created' | 'documents'>('created')
  const [loSortDir, setLoSortDir] = useState<'asc' | 'desc'>('desc')
  const queryClient = useQueryClient()

  const { data: analysisHistoryData, isLoading } = useQuery({
    queryKey: ['lo-history', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', LO_ANALYSIS_TYPE),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  const { data: bulkAnalysesData, isLoading: isBulkLoading } = useQuery({
    queryKey: ['bulk-analyses-for-lo', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', 'bulk analyzer'),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  const byDateDesc = (a: AnalysisSummary, b: AnalysisSummary) =>
    new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()

  const loAnalyses: AnalysisSummary[] = (analysisHistoryData?.analyses ?? []).filter(a => (a.document_count ?? 0) > 1).sort(byDateDesc)
  const bulkAnalyses: AnalysisSummary[] = (bulkAnalysesData?.analyses ?? []).sort(byDateDesc)

  const sortedLoAnalyses = useMemo(() => [...loAnalyses].sort((a, b) => {
    let av: any, bv: any
    if (loSortCol === 'created') { av = new Date(a.created_at || 0).getTime(); bv = new Date(b.created_at || 0).getTime() }
    else { av = (a.filenames?.[0] || '').toLowerCase(); bv = (b.filenames?.[0] || '').toLowerCase() }
    if (av < bv) return loSortDir === 'asc' ? -1 : 1
    if (av > bv) return loSortDir === 'asc' ? 1 : -1
    return 0
  }), [loAnalyses, loSortCol, loSortDir])

  const handleLoSort = (col: 'created' | 'documents') => {
    if (loSortCol === col) { setLoSortDir(d => d === 'asc' ? 'desc' : 'asc') }
    else { setLoSortCol(col); setLoSortDir('asc') }
    setLoPage(1)
  }

  const LoSortIcon = ({ col }: { col: 'created' | 'documents' }) => {
    if (loSortCol !== col) return <span className="text-blue-600">⇅</span>
    return <span className="text-blue-600">{loSortDir === 'asc' ? '↑' : '↓'}</span>
  }

  const [loPage, setLoPage] = useState(1)
  const [loPageSize, setLoPageSize] = useState(25)
  const loPaged = sortedLoAnalyses.slice((loPage - 1) * loPageSize, loPage * loPageSize)

  const [bulkPage, setBulkPage] = useState(1)
  const [bulkPageSize, setBulkPageSize] = useState(25)
  const bulkPaged = bulkAnalyses.slice((bulkPage - 1) * bulkPageSize, bulkPage * bulkPageSize)

  const handleView = async (a: AnalysisSummary) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) return
    setViewingAnalysisId(a.analysis_id)
    setViewLoading(true)
    setViewError(null)
    setViewResult(null)
    try {
      const detail = await analysisHistoryApi.getAnalysis(
        selectedOrganizationId,
        selectedWorkspaceId,
        a.analysis_id,
        true
      )
      const objectives = extractObjectivesFromAnalysisData(detail.analysis_data)
      if (objectives.length === 0) {
        setViewError('No learning outcomes found in this analysis.')
        return
      }
      const sourceLabel = a.filenames?.join(', ') || `Analysis ${a.analysis_id.slice(0, 8)}…`
      setViewResult({ sourceLabel, objectives, documentCount: a.document_count })
    } catch (e: any) {
      setViewError(e?.message || 'Failed to load analysis.')
    } finally {
      setViewLoading(false)
    }
  }

  const handleDownload = async (a: AnalysisSummary) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) return
    setDownloadingPdfFor(a.analysis_id)
    try {
      const detail = await analysisHistoryApi.getAnalysis(
        selectedOrganizationId,
        selectedWorkspaceId,
        a.analysis_id,
        true
      )
      if (!detail.analysis_data) {
        alert('No analysis data available for download.')
        return
      }
      const docName = a.filenames?.join(', ') || `LO Analysis ${a.analysis_id.slice(0, 8)}`
      await exportToAccessiblePDF(
        {
          status: 'completed',
          filename: docName,
          document_type: 'learning_objectives',
          analyzedBy: a.created_by,
          analysisDate: a.completed_at,
          ...detail.analysis_data,
        },
        { documentName: docName }
      )
    } catch (e: any) {
      alert(e?.message || 'Failed to download PDF.')
    } finally {
      setDownloadingPdfFor(null)
    }
  }

  // Download from a bulk analysis — only pass LO-relevant fields so only objectives + chart render
  const handleDownloadBulk = async (a: AnalysisSummary) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) return
    setDownloadingPdfFor(a.analysis_id)
    try {
      const detail = await analysisHistoryApi.getAnalysis(
        selectedOrganizationId,
        selectedWorkspaceId,
        a.analysis_id,
        true
      )
      if (!detail.analysis_data?.learning_objectives?.length) {
        alert('No learning outcomes found in this analysis.')
        return
      }
      const docName = a.filenames?.join(', ') || `Analysis ${a.analysis_id.slice(0, 8)}`
      await exportToAccessiblePDF(
        {
          status: 'completed',
          filename: docName,
          document_type: 'learning_objectives',
          analyzedBy: a.created_by,
          analysisDate: a.completed_at,
          learning_objectives: detail.analysis_data.learning_objectives,
          document_metadata: detail.analysis_data.document_metadata,
        },
        { documentName: docName }
      )
    } catch (e: any) {
      alert(e?.message || 'Failed to download PDF.')
    } finally {
      setDownloadingPdfFor(null)
    }
  }

  const handleCloseView = () => {
    setViewResult(null)
    setViewingAnalysisId(null)
    setViewError(null)
  }

  const handleDelete = (analysisId: string) => {
    setAnalysisToDelete(analysisId)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = async () => {
    if (!analysisToDelete || !selectedOrganizationId || !selectedWorkspaceId) return
    setDeletingId(analysisToDelete)
    setDeleteConfirmOpen(false)
    setAnalysisToDelete(null)
    try {
      await analysisHistoryApi.deleteAnalysis(selectedOrganizationId, selectedWorkspaceId, analysisToDelete)
      void queryClient.invalidateQueries({ queryKey: ['lo-history', selectedOrganizationId, selectedWorkspaceId] })
    } catch (e: any) {
      alert(e?.message || 'Failed to delete analysis.')
    } finally {
      setDeletingId(null)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':  return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'failed':     return <XCircle className="h-5 w-5 text-red-500" />
      case 'processing': return <Clock className="h-5 w-5 text-yellow-500 animate-spin" />
      default:           return <Clock className="h-5 w-5 text-gray-400" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':  return 'bg-green-100 text-green-800'
      case 'failed':     return 'bg-red-100 text-red-800'
      case 'processing': return 'bg-yellow-100 text-yellow-800'
      default:           return 'bg-gray-100 text-gray-800'
    }
  }

  const fmtDate = (iso?: string) => {
    if (!iso) return '—'
    const s = iso.includes('+') || iso.endsWith('Z') ? iso : iso + 'Z'
    return formatInTimeZone(new Date(s), 'America/New_York', 'MMM d, yyyy h:mm a')
  }

  return (
    <div className="space-y-6">
      {/* History table — hidden while viewing a result */}
      {!viewResult && <div className="bg-white p-6 rounded-xl shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">LO Analysis History</h2>
          <span className="text-sm text-gray-500">{loAnalyses.length} analyses</span>
        </div>
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : loAnalyses.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No learning outcome analyses found.</p>
            <p className="text-sm text-gray-500 mt-2">
              Run an extraction from the "Extract from Documents" tab to get started.
            </p>
          </div>
        ) : (
          <>
          <PaginationTop total={loAnalyses.length} page={loPage} pageSize={loPageSize} label="analyses"
            onPageSizeChange={n => { setLoPageSize(n); setLoPage(1) }} />
          <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th onClick={() => handleLoSort('created')} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                    <div className="flex items-center gap-2">Created <LoSortIcon col="created" /></div>
                  </th>
                  <th onClick={() => handleLoSort('documents')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                    <div className="flex items-center gap-2">Documents <LoSortIcon col="documents" /></div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                    Actions
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prompt
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loPaged.map(a => (
                  <tr
                    key={a.analysis_id}
                    className={`hover:bg-gray-50 transition-colors ${
                      viewingAnalysisId === a.analysis_id ? 'bg-blue-50' : ''
                    }`}
                  >
                    {/* Created */}
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{fmtDate(a.created_at)}</div>
                    </td>

                    {/* Documents */}
                    <td className="px-4 py-4">
                      {a.filenames && a.filenames.length > 0 ? (
                        <ul className="text-xs text-gray-600 space-y-0.5">
                          {a.filenames.map((f, i) => (
                            <li key={i} className="truncate max-w-xs" title={f}>{f}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-sm text-gray-900 font-medium">
                          {a.document_count} {a.document_count === 1 ? 'document' : 'documents'}
                        </span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(a.status)}
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(a.status)}`}>
                          {a.status}
                        </span>
                      </div>
                      {a.completed_at && (
                        <div className="text-xs text-gray-500 mt-1">{fmtDate(a.completed_at)}</div>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {a.status === 'completed' && (
                          <>
                            <button
                              onClick={() => handleView(a)}
                              disabled={viewLoading && viewingAnalysisId === a.analysis_id}
                              className={`p-1 rounded transition-colors disabled:opacity-50 ${
                                viewingAnalysisId === a.analysis_id
                                  ? 'text-blue-600 bg-blue-100'
                                  : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                              }`}
                              title="View learning outcomes"
                            >
                              {viewLoading && viewingAnalysisId === a.analysis_id ? (
                                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDownload(a)}
                              disabled={downloadingPdfFor === a.analysis_id}
                              className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                              title="Download PDF"
                            >
                              {downloadingPdfFor === a.analysis_id
                                ? <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full" />
                                : <Download className="h-4 w-4" />
                              }
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(a.analysis_id)}
                          disabled={deletingId === a.analysis_id}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Delete analysis"
                        >
                          {deletingId === a.analysis_id
                            ? <div className="animate-spin h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full" />
                            : <Trash2 className="h-4 w-4" />
                          }
                        </button>
                      </div>
                    </td>

                    {/* Prompt */}
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{a.prompt_name || '—'}</div>
                      {a.analysis_type && (
                        <div className="text-xs text-gray-500">{a.analysis_type}</div>
                      )}
                      {a.prompt_type && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                          {a.prompt_type}
                        </span>
                      )}
                    </td>

                    {/* Created By */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{a.created_by || '—'}</div>
                    </td>

                    {/* Cost */}
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {a.llm_cost ? `$${parseFloat(a.llm_cost).toFixed(4)}` : '—'}
                      </div>
                      {a.result_size_mb && (
                        <div className="text-xs text-gray-500 mt-1">
                          {parseFloat(a.result_size_mb).toFixed(2)} MB
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationBottom total={loAnalyses.length} page={loPage} pageSize={loPageSize}
            onPrev={() => setLoPage(p => Math.max(1, p - 1))}
            onNext={() => setLoPage(p => Math.min(Math.ceil(loAnalyses.length / loPageSize), p + 1))} />
          </>
        )}
      </div>}

      {/* Bulk Analyses section — LO data extracted from regular analysis runs */}
      {!viewResult && <div className="bg-white p-6 rounded-xl shadow-lg">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-800">From Bulk Analysis History</h2>
          <span className="text-sm text-gray-500">{bulkAnalyses.length} analyses</span>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Learning outcomes extracted from completed bulk analysis runs. Eye icon and download show only the LO section.
        </p>

        {isBulkLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : bulkAnalyses.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No bulk analyses found.</p>
            <p className="text-sm text-gray-500 mt-2">
              Run a bulk analysis from the Analysis tab to see results here.
            </p>
          </div>
        ) : (
          <>
          <PaginationTop total={bulkAnalyses.length} page={bulkPage} pageSize={bulkPageSize} label="analyses"
            onPageSizeChange={n => { setBulkPageSize(n); setBulkPage(1) }} />
          <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documents</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prompt</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bulkPaged.map(a => (
                  <tr
                    key={a.analysis_id}
                    className={`hover:bg-gray-50 transition-colors ${viewingAnalysisId === a.analysis_id ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{fmtDate(a.created_at)}</div>
                    </td>
                    <td className="px-4 py-4">
                      {a.filenames && a.filenames.length > 0 ? (
                        <ul className="text-xs text-gray-600 space-y-0.5">
                          {a.filenames.map((f, i) => (
                            <li key={i} className="truncate max-w-xs" title={f}>{f}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-sm text-gray-900 font-medium">
                          {a.document_count} {a.document_count === 1 ? 'document' : 'documents'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(a.status)}
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(a.status)}`}>
                          {a.status}
                        </span>
                      </div>
                      {a.completed_at && (
                        <div className="text-xs text-gray-500 mt-1">{fmtDate(a.completed_at)}</div>
                      )}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleView(a)}
                          disabled={viewLoading && viewingAnalysisId === a.analysis_id}
                          className={`p-1 rounded transition-colors disabled:opacity-50 ${
                            viewingAnalysisId === a.analysis_id
                              ? 'text-blue-600 bg-blue-100'
                              : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                          }`}
                          title="View learning outcomes"
                        >
                          {viewLoading && viewingAnalysisId === a.analysis_id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDownloadBulk(a)}
                          disabled={downloadingPdfFor === a.analysis_id}
                          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                          title="Download LO PDF"
                        >
                          {downloadingPdfFor === a.analysis_id
                            ? <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full" />
                            : <Download className="h-4 w-4" />
                          }
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{a.prompt_name || '—'}</div>
                      {a.analysis_type && <div className="text-xs text-gray-500">{a.analysis_type}</div>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{a.created_by || '—'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationBottom total={bulkAnalyses.length} page={bulkPage} pageSize={bulkPageSize}
            onPrev={() => setBulkPage(p => Math.max(1, p - 1))}
            onNext={() => setBulkPage(p => Math.min(Math.ceil(bulkAnalyses.length / bulkPageSize), p + 1))} />
          </>
        )}
      </div>}

      {/* Result viewer */}
      {viewError && !viewLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <span className="text-red-500">⚠️</span>
          <p className="text-sm text-red-700 flex-1">{viewError}</p>
          <button onClick={handleCloseView} className="text-red-400 hover:text-red-600">
            <X size={16} />
          </button>
        </div>
      )}

      {viewResult && !viewLoading && (
        <div className="space-y-4">
          <button
            onClick={handleCloseView}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to History
          </button>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-bold text-gray-800">{viewResult.sourceLabel}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {viewResult.objectives.length} primary outcome
              {viewResult.objectives.length !== 1 ? 's' : ''} extracted
              {viewResult.documentCount && viewResult.documentCount > 1
                ? ` · ${viewResult.documentCount} documents`
                : ''}
            </p>
          </div>
          <LearningObjectivesSimplified
            objectives={viewResult.objectives}
            documentCount={viewResult.documentCount}
          />
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setAnalysisToDelete(null) }}
        onConfirm={confirmDelete}
        title="Delete LO Analysis"
        message="Are you sure you want to delete this learning outcomes analysis? This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  )
}

// ─── Extract Tab ──────────────────────────────────────────────────────────────
type SortColumn = 'filename' | 'type' | 'status' | 'analyzed_by' | 'analyzed_date' | 'size' | 'uploaded'

function ExtractTab({ selectedFolder }: { selectedFolder: string }) {
  const { selectedOrganizationId, selectedWorkspaceId, userId, user } = useAuth()
  const queryClient = useQueryClient()

  // Selection
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([])

  // Table controls
  const [searchQuery, setSearchQuery] = useState('')
  const [sortColumn, setSortColumn] = useState<SortColumn>('uploaded')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Job / result state
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<string | null>(null)
  const [processingDocIds, setProcessingDocIds] = useState<string[]>([])
  const [result, setResult] = useState<{
    sourceLabel: string
    objectives: any[]
    documentCount?: number
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  // View existing doc analysis state (eye icon in table)
  const [viewDocResult, setViewDocResult] = useState<{
    sourceLabel: string
    objectives: any[]
    documentCount?: number
  } | null>(null)
  const [viewDocError, setViewDocError] = useState<string | null>(null)
  const [downloadingPdfFor, setDownloadingPdfFor] = useState<string | null>(null)

  const { data: documents = [], isLoading: docsLoading } = useQuery<Document[]>({
    queryKey: ['documents', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => documentsApi.getDocuments(selectedOrganizationId!, selectedWorkspaceId!),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
    refetchInterval: 5000,
  })

  const { data: workspaceConfig } = useQuery({
    queryKey: ['workspaceConfig', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => configApi.getWorkspaceConfig(selectedOrganizationId!, selectedWorkspaceId!),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // ── Table helpers ──────────────────────────────────────────────────────────

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploaded':   return 'bg-blue-100 text-blue-800'
      case 'processing': return 'bg-yellow-100 text-yellow-800'
      case 'completed':  return 'bg-green-100 text-green-800'
      case 'failed':     return 'bg-red-100 text-red-800'
      default:           return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'uploaded':   return 'Ready'
      case 'processing': return 'Processing'
      case 'completed':  return 'Analyzed'
      case 'failed':     return 'Failed'
      default:           return status
    }
  }

  const handleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(col)
      setSortDirection('asc')
    }
    setCurrentPage(1)
  }

  const SortIcon = ({ col }: { col: SortColumn }) => {
    if (sortColumn !== col) return <span className="text-blue-600">⇅</span>
    return <span className="text-blue-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>
  }

  const handleSearchChange = (v: string) => {
    setSearchQuery(v)
    setCurrentPage(1)
  }

  // ── Filtered / sorted / paginated list ────────────────────────────────────

  const filteredDocs = useMemo(() => {
    const q = searchQuery.toLowerCase()
    return documents.filter(doc => {
      const matchesFolder = selectedFolder === 'all' || doc.folder === selectedFolder
      const matchesSearch =
        q === '' ||
        doc.original_filename.toLowerCase().includes(q) ||
        doc.document_type.toLowerCase().includes(q) ||
        doc.processing_status.toLowerCase().includes(q) ||
        (doc.folder || '').toLowerCase().includes(q)
      return matchesFolder && matchesSearch
    })
  }, [documents, searchQuery, selectedFolder])

  const sortedDocs = useMemo(() => {
    return [...filteredDocs].sort((a, b) => {
      let av: any, bv: any
      switch (sortColumn) {
        case 'filename':      av = a.original_filename.toLowerCase(); bv = b.original_filename.toLowerCase(); break
        case 'type':          av = a.document_type.toLowerCase();     bv = b.document_type.toLowerCase();     break
        case 'status':        av = a.processing_status.toLowerCase(); bv = b.processing_status.toLowerCase(); break
        case 'analyzed_by':   av = (a.learningobjectives_data?.document_metadata?.analyzed_by || a.uploaded_by_username || a.uploaded_by || '').toLowerCase(); bv = (b.learningobjectives_data?.document_metadata?.analyzed_by || b.uploaded_by_username || b.uploaded_by || '').toLowerCase(); break
        case 'analyzed_date': av = a.learningobjectives_data?.document_metadata?.analysis_timestamp ? new Date(a.learningobjectives_data.document_metadata.analysis_timestamp).getTime() : 0; bv = b.learningobjectives_data?.document_metadata?.analysis_timestamp ? new Date(b.learningobjectives_data.document_metadata.analysis_timestamp).getTime() : 0; break
        case 'size':          av = a.file_size_bytes;                 bv = b.file_size_bytes;                 break
        case 'uploaded':      av = new Date(a.uploaded_at).getTime(); bv = new Date(b.uploaded_at).getTime(); break
        default: return 0
      }
      if (av < bv) return sortDirection === 'asc' ? -1 : 1
      if (av > bv) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredDocs, sortColumn, sortDirection])

  const totalPages = Math.ceil(sortedDocs.length / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const pagedDocs = sortedDocs.slice(startIndex, startIndex + pageSize)

  // ── Selection ──────────────────────────────────────────────────────────────

  const toggleDoc = (id: string) => {
    setSelectedDocIds(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    )
  }

  // ── PDF Download ───────────────────────────────────────────────────────────

  const handleDownloadLOPDF = async (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!doc.learningobjectives_data) return
    setDownloadingPdfFor(doc.document_id)
    try {
      await exportToAccessiblePDF(
        {
          status: 'completed',
          filename: doc.original_filename,
          document_type: doc.document_type,
          analyzedBy: doc.learningobjectives_data.document_metadata?.analyzed_by || doc.uploaded_by_username || doc.uploaded_by,
          analysisDate: doc.learningobjectives_data.document_metadata?.analysis_timestamp,
          ...doc.learningobjectives_data,
        },
        { documentName: doc.original_filename }
      )
    } catch {
      alert('Failed to download PDF. Please try again.')
    } finally {
      setDownloadingPdfFor(null)
    }
  }

  // ── Extraction ────────────────────────────────────────────────────────────

  // Always uses multiAnalyzeDocuments (even for 1 doc) so every LO extraction
  // creates its own entry in analysis-history and never overwrites the document record.
  const handleRunExtraction = async () => {
    if (!selectedOrganizationId || !selectedWorkspaceId || !userId || selectedDocIds.length === 0)
      return

    setError(null)
    setResult(null)
    setProcessingDocIds(selectedDocIds)
    setJobStatus('PENDING')

    const username = (user as any)?.userInfo?.username || (user as any)?.name || userId

    try {
      const res = await documentsApi.multiAnalyzeDocuments(
        selectedDocIds,
        selectedOrganizationId,
        selectedWorkspaceId,
        userId,
        username,
        'default',
        LO_PROMPT_NAME,
        LO_ANALYSIS_TYPE,
        'default',
        LO_SCHEMA_NAME,
        workspaceConfig?.default_llm_model
      )
      setCurrentJobId(res.job_id)
    } catch (e: any) {
      setError(e?.message || 'Failed to start extraction job.')
      setProcessingDocIds([])
      setJobStatus(null)
    }
  }

  const handleJobComplete = async () => {
    try {
      if (processingDocIds.length === 1) {
        // Single-doc LO: result is saved to learningobjectives_data on the document
        // and also to analysis history.
        const updatedDocs = await documentsApi.getDocuments(selectedOrganizationId!, selectedWorkspaceId!)
        const doc = updatedDocs.find(d => d.document_id === processingDocIds[0])
        if (doc?.learningobjectives_data) {
          const objectives = extractObjectivesFromAnalysisData(doc.learningobjectives_data)
          setResult({
            sourceLabel: doc.original_filename,
            objectives,
            documentCount: 1,
          })
          void queryClient.invalidateQueries({
            queryKey: ['documents', selectedOrganizationId, selectedWorkspaceId],
          })
          void queryClient.invalidateQueries({
            queryKey: ['lo-history', selectedOrganizationId, selectedWorkspaceId],
          })
        } else {
          setError('Analysis completed but learning outcomes data could not be retrieved.')
        }
      } else {
        // Multi-doc LO: result is saved to analysis-history — look it up there.
        const analysesData = await analysisHistoryApi.listAnalyses(
          selectedOrganizationId!,
          selectedWorkspaceId!,
          10,
          'completed'
        )
        const match = analysesData.analyses
          .filter(a => {
            const ids = a.document_ids || []
            return (
              a.analysis_type === LO_ANALYSIS_TYPE &&
              ids.length === processingDocIds.length &&
              processingDocIds.every(id => ids.includes(id))
            )
          })
          .sort(
            (a, b) =>
              new Date(b.completed_at || 0).getTime() - new Date(a.completed_at || 0).getTime()
          )[0]

        if (match) {
          const detail = await analysisHistoryApi.getAnalysis(
            selectedOrganizationId!,
            selectedWorkspaceId!,
            match.analysis_id,
            true
          )
          const objectives = extractObjectivesFromAnalysisData(detail.analysis_data)
          const names = documents
            .filter(d => processingDocIds.includes(d.document_id))
            .map(d => d.original_filename)
            .join(', ')
          setResult({
            sourceLabel: names || `${processingDocIds.length} document(s)`,
            objectives,
            documentCount: processingDocIds.length,
          })
          void queryClient.invalidateQueries({
            queryKey: ['lo-history', selectedOrganizationId, selectedWorkspaceId],
          })
          void queryClient.invalidateQueries({
            queryKey: ['bulk-lo-for-plo', selectedOrganizationId, selectedWorkspaceId],
          })
          void queryClient.invalidateQueries({
            queryKey: ['bulk-lo-for-plo-mapping', selectedOrganizationId, selectedWorkspaceId],
          })
        } else {
          setError('Analysis completed but result could not be retrieved.')
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to retrieve extraction results.')
    } finally {
      setCurrentJobId(null)
      setJobStatus(null)
      setProcessingDocIds([])
    }
  }

  const handleJobError = () => {
    setError('Learning outcomes extraction failed. Please try again.')
    setCurrentJobId(null)
    setJobStatus(null)
    setProcessingDocIds([])
  }

  const isProcessing = processingDocIds.length > 0

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {currentJobId && (
        <JobPoller
          jobId={currentJobId}
          jobContext={{
            org_id: selectedOrganizationId || undefined,
            ws_id: selectedWorkspaceId || undefined,
          }}
          onStatusChange={setJobStatus}
          onComplete={handleJobComplete}
          onError={handleJobError}
        />
      )}

      {/* Job status banner */}
      {isProcessing && (
        <JobStatus
          jobId={currentJobId}
          jobStatus={jobStatus}
          isProcessing={isProcessing}
          processDescription={`Extracting learning outcomes from ${processingDocIds.length} document${processingDocIds.length !== 1 ? 's' : ''}...`}
        />
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <span className="text-red-500 text-lg">⚠️</span>
          <div>
            <p className="text-sm font-medium text-red-800">Could not extract learning outcomes</p>
            <p className="text-sm text-red-600 mt-0.5">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Result panel */}
      {result && !isProcessing && (
        <div className="space-y-4">
          <button
            onClick={() => { setResult(null); setSelectedDocIds([]) }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Documents
          </button>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-bold text-gray-800">{result.sourceLabel}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {result.objectives.length} primary outcome
              {result.objectives.length !== 1 ? 's' : ''} extracted
              {result.documentCount && result.documentCount > 1
                ? ` · ${result.documentCount} documents`
                : ''}
            </p>
          </div>
          <LearningObjectivesSimplified
            objectives={result.objectives}
            documentCount={result.documentCount}
          />
        </div>
      )}

      {/* View-doc error banner */}
      {viewDocError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <span className="text-red-500 text-lg">⚠️</span>
          <p className="text-sm text-red-700 flex-1">{viewDocError}</p>
          <button onClick={() => setViewDocError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* View-doc result panel */}
      {viewDocResult && (
        <div className="space-y-4">
          <button
            onClick={() => setViewDocResult(null)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Documents
          </button>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-xl font-bold text-gray-800">{viewDocResult.sourceLabel}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {viewDocResult.objectives.length} primary outcome
              {viewDocResult.objectives.length !== 1 ? 's' : ''} extracted
            </p>
          </div>
          <LearningObjectivesSimplified
            objectives={viewDocResult.objectives}
            documentCount={viewDocResult.documentCount}
          />
        </div>
      )}

      {/* Document table — hidden while viewing a result or job is running */}
      {!result && !viewDocResult && !isProcessing && <div className="bg-white p-8 rounded-xl shadow-lg">
        <div className="flex justify-between items-start mb-6 gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {selectedFolder === 'all' ? 'Select Documents to Analyze' : `${selectedFolder.toUpperCase()} Documents`}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Each extraction run creates a new saved entry in your LO history.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {/* Search bar */}
            <div className="relative w-72">
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button onClick={() => handleSearchChange('')} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              )}
            </div>

            <button
              onClick={handleRunExtraction}
              disabled={isProcessing || selectedDocIds.length === 0}
              className="shrink-0 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center gap-2"
            >
              {isProcessing ? (
                <><Loader2 className="animate-spin h-4 w-4" /> Extracting...</>
              ) : (
                <>Extract Learning Outcomes ({selectedDocIds.length} selected)</>
              )}
            </button>
          </div>
        </div>

        {docsLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-600">Loading documents...</p>
            </div>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12">
            <span className="text-4xl block mb-3">📄</span>
            <h3 className="text-lg font-semibold text-gray-700 mb-1">No Documents Found</h3>
            <p className="text-gray-500 text-sm">Please upload documents in the Documents tab first.</p>
          </div>
        ) : (
          <>
            {/* Pagination top row */}
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-600">
                {searchQuery
                  ? <>Found {sortedDocs.length} of {documents.length} documents</>
                  : <>Showing {startIndex + 1}–{Math.min(startIndex + pageSize, sortedDocs.length)} of {sortedDocs.length} documents</>
                }
                {selectedDocIds.length > 0 && (
                  <span className="ml-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                    {selectedDocIds.length} selected
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Rows per page:</label>
                <select
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {/* Select all toggle */}
            {sortedDocs.length > 0 && (
              <label className="flex items-center gap-2 mb-3 w-fit text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  checked={selectedDocIds.length === sortedDocs.length}
                  ref={el => { if (el) el.indeterminate = selectedDocIds.length > 0 && selectedDocIds.length < sortedDocs.length }}
                  onChange={() => setSelectedDocIds(
                    selectedDocIds.length === sortedDocs.length ? [] : sortedDocs.map(d => d.document_id)
                  )}
                />
                {selectedDocIds.length === sortedDocs.length ? 'Deselect all' : `Select all (${sortedDocs.length})`}
              </label>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      Select
                    </th>
                    <th
                      onClick={() => handleSort('filename')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">Filename <SortIcon col="filename" /></div>
                    </th>
                    <th
                      onClick={() => handleSort('type')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">Type <SortIcon col="type" /></div>
                    </th>
                    <th
                      onClick={() => handleSort('status')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">Status <SortIcon col="status" /></div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Actions
                    </th>
                    <th
                      onClick={() => handleSort('analyzed_by')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">Analyzed By <SortIcon col="analyzed_by" /></div>
                    </th>
                    <th
                      onClick={() => handleSort('analyzed_date')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">Analyzed Date <SortIcon col="analyzed_date" /></div>
                    </th>
                    <th
                      onClick={() => handleSort('size')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">Size <SortIcon col="size" /></div>
                    </th>
                    <th
                      onClick={() => handleSort('uploaded')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">Uploaded <SortIcon col="uploaded" /></div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagedDocs.map(doc => {
                    const isReady = doc.processing_status === 'uploaded' || doc.processing_status === 'completed'
                    const isSelected = selectedDocIds.includes(doc.document_id)
                    return (
                      <tr
                        key={doc.document_id}
                        onClick={() => isReady && toggleDoc(doc.document_id)}
                        className={`transition-colors ${
                          !isReady
                            ? 'bg-gray-50 cursor-not-allowed opacity-60'
                            : isSelected
                              ? 'bg-blue-50 cursor-pointer'
                              : 'hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        {/* Checkbox cell */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                            !isReady
                              ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                              : isSelected
                                ? 'bg-blue-500 border-blue-500'
                                : 'border-gray-300'
                          }`}>
                            {isSelected && isReady && (
                              <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </td>

                        {/* Filename */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-blue-500 shrink-0" />
                            <span className="text-sm font-medium text-gray-900">{doc.original_filename}</span>
                          </div>
                        </td>

                        {/* Type */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700 capitalize">{doc.document_type}</span>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(doc.processing_status)}`}>
                              {getStatusText(doc.processing_status)}
                            </span>
                            {doc.processing_status === 'completed' && (
                              <CheckCircle className="h-4 w-4 text-green-600" aria-label="Previously analyzed" />
                            )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            {doc.learningobjectives_data ? (
                              <>
                                <button
                                  onClick={e => {
                                    e.stopPropagation()
                                    const objectives = extractObjectivesFromAnalysisData(doc.learningobjectives_data)
                                    if (objectives.length === 0) {
                                      setViewDocError(`No learning outcomes found for "${doc.original_filename}".`)
                                      setViewDocResult(null)
                                    } else {
                                      setViewDocError(null)
                                      setViewDocResult({
                                        sourceLabel: doc.original_filename,
                                        objectives,
                                        documentCount: 1,
                                      })
                                    }
                                  }}
                                  className="p-1 hover:bg-blue-100 rounded transition-colors"
                                  title="View Learning Outcomes"
                                >
                                  <Eye className="h-4 w-4 text-blue-600" />
                                </button>
                                <button
                                  onClick={e => handleDownloadLOPDF(doc, e)}
                                  disabled={downloadingPdfFor === doc.document_id}
                                  className="p-1 hover:bg-green-100 rounded transition-colors disabled:opacity-50"
                                  title="Download PDF"
                                >
                                  {downloadingPdfFor === doc.document_id
                                    ? <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full" />
                                    : <Download className="h-4 w-4 text-green-600" />
                                  }
                                </button>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>
                        </td>

                        {/* Analyzed By */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700">
                            {doc.learningobjectives_data?.document_metadata?.analyzed_by || doc.uploaded_by_username || doc.uploaded_by || '—'}
                          </span>
                        </td>

                        {/* Analyzed Date */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700">
                            {doc.learningobjectives_data?.document_metadata?.analysis_timestamp
                              ? formatInTimeZone(new Date(doc.learningobjectives_data.document_metadata.analysis_timestamp), 'America/New_York', 'MMM d, yyyy h:mm a')
                              : '—'}
                          </span>
                        </td>

                        {/* Size */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700">
                            {(doc.file_size_bytes / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </td>

                        {/* Uploaded */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700">
                            {format(new Date(doc.uploaded_at), 'MMM d, yyyy')}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {sortedDocs.length === 0 && (searchQuery || selectedFolder !== 'all') && (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">
                  {searchQuery
                    ? `No documents found matching "${searchQuery}"`
                    : `No documents in folder "${selectedFolder}"`}
                </p>
              </div>
            )}

            {/* Pagination bottom */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center pt-4 border-t mt-4">
                <div className="text-sm text-gray-600">Page {currentPage} of {totalPages}</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}

          </>
        )}
      </div>}
    </div>
  )
}

// ─── CLO Comparison Tab ───────────────────────────────────────────────────────
function CLOComparisonTab() {
  const { selectedOrganizationId, selectedWorkspaceId, userId } = useAuth()

  // Selection
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [labels, setLabels] = useState<Record<string, string>>({})

  // Prerequisite check mode
  const [isPrerequisite, setIsPrerequisite] = useState(false)
  // In prerequisite mode, one or more selected analyses are prerequisites and exactly
  // one (requiringId) is the requiring course. All others are treated as prerequisites.
  const [requiringId, setRequiringId] = useState<string | null>(null)

  // Job tracking
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Result
  const [comparisonResult, setComparisonResult] = useState<any>(null)

  // View past comparison
  const [viewingComparisonId, setViewingComparisonId] = useState<string | null>(null)
  const [viewLoading, setViewLoading] = useState(false)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [analysisToDelete, setAnalysisToDelete] = useState<string | null>(null)
  const [showOrderConfirm, setShowOrderConfirm] = useState(false)
  const [cloSortCol, setCloSortCol] = useState<'type' | 'created' | 'documents' | 'completed'>('created')
  const [cloSortDir, setCloSortDir] = useState<'asc' | 'desc'>('desc')

  const queryClient = useQueryClient()

  const { data: workspaceConfig } = useQuery({
    queryKey: ['workspaceConfig', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => configApi.getWorkspaceConfig(selectedOrganizationId!, selectedWorkspaceId!),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Bulk LO analyses available for selection (document_count > 1)
  const { data: loHistoryData, isLoading: loHistoryLoading } = useQuery({
    queryKey: ['lo-history', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', LO_ANALYSIS_TYPE),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Individual documents with a completed LO extraction
  const { data: cloDocuments = [], isLoading: cloDocsLoading } = useQuery<Document[]>({
    queryKey: ['documents', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => documentsApi.getDocuments(selectedOrganizationId!, selectedWorkspaceId!),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Bulk analyses from the main Analysis tab — their LO section is reusable here (document_count > 1)
  const { data: cloBulkAnalysesData, isLoading: cloBulkAnalysesLoading } = useQuery({
    queryKey: ['bulk-analyses-for-clo', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', 'bulk analyzer'),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Past CLO comparisons (both types)
  const { data: pastComparisonsData, isLoading: pastCloLoading } = useQuery({
    queryKey: ['clo-comparisons', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', 'clo comparison'),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  const { data: pastPrereqData, isLoading: pastPrereqLoading } = useQuery({
    queryKey: ['prereq-checks', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', 'prerequisite check'),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  const byDateDesc = (a: AnalysisSummary, b: AnalysisSummary) =>
    new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()

  // Unified source type for CLO tab: analysis history items + individual documents with LO data
  type CloSource = { id: string; sourceType: 'bulk-lo' | 'lo' | 'bulk-analysis'; created_at?: string; completed_at?: string; filenames?: string[]; document_count?: number; folder?: string; created_by?: string }

  const cloBulkLoAnalyses = (loHistoryData?.analyses ?? []).filter(a => (a.document_count ?? 0) > 1).sort(byDateDesc)
  const cloDocsWithLO = cloDocuments.filter(d => d.learningobjectives_data)
  const cloBulkAnalyses = (cloBulkAnalysesData?.analyses ?? []).filter(a => (a.document_count ?? 0) > 1).sort(byDateDesc)

  const cloAllSources: CloSource[] = [
    ...cloBulkLoAnalyses.map(a => ({ id: a.analysis_id, sourceType: 'bulk-lo' as const, created_at: a.created_at, completed_at: a.completed_at, filenames: a.filenames, document_count: a.document_count, created_by: a.created_by })),
    ...cloBulkAnalyses.map(a => ({ id: a.analysis_id, sourceType: 'bulk-analysis' as const, created_at: a.created_at, completed_at: a.completed_at, filenames: a.filenames, document_count: a.document_count, created_by: a.created_by })),
    ...cloDocsWithLO.map(d => ({ id: d.document_id, sourceType: 'lo' as const, created_at: d.uploaded_at, completed_at: d.learningobjectives_data?.document_metadata?.analysis_timestamp, filenames: [d.original_filename], document_count: 1, folder: d.folder, created_by: d.uploaded_by_username || d.uploaded_by })),
  ].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())

  const sortedCloSources = useMemo(() => [...cloAllSources].sort((a, b) => {
    let av: any, bv: any
    if (cloSortCol === 'type') { av = a.sourceType; bv = b.sourceType }
    else if (cloSortCol === 'created') { av = new Date(a.created_at || 0).getTime(); bv = new Date(b.created_at || 0).getTime() }
    else if (cloSortCol === 'documents') { av = (a.filenames?.[0] || '').toLowerCase(); bv = (b.filenames?.[0] || '').toLowerCase() }
    else { av = new Date(a.completed_at || 0).getTime(); bv = new Date(b.completed_at || 0).getTime() }
    if (av < bv) return cloSortDir === 'asc' ? -1 : 1
    if (av > bv) return cloSortDir === 'asc' ? 1 : -1
    return 0
  }), [cloAllSources, cloSortCol, cloSortDir])

  const handleCloSort = (col: 'type' | 'created' | 'documents' | 'completed') => {
    if (cloSortCol === col) { setCloSortDir(d => d === 'asc' ? 'desc' : 'asc') }
    else { setCloSortCol(col); setCloSortDir('asc') }
    setLoPickerPage(1)
  }

  const CloSortIcon = ({ col }: { col: 'type' | 'created' | 'documents' | 'completed' }) => {
    if (cloSortCol !== col) return <span className="text-blue-600">⇅</span>
    return <span className="text-blue-600">{cloSortDir === 'asc' ? '↑' : '↓'}</span>
  }

  const loLoading = loHistoryLoading || cloDocsLoading || cloBulkAnalysesLoading
  const pastLoading = pastCloLoading || pastPrereqLoading
  const pastComparisons: AnalysisSummary[] = [
    ...(pastComparisonsData?.analyses ?? []),
    ...(pastPrereqData?.analyses ?? []),
  ].sort(byDateDesc)

  const [loPickerPage, setLoPickerPage] = useState(1)
  const [loPickerPageSize, setLoPickerPageSize] = useState(25)
  const loPickerPaged = sortedCloSources.slice((loPickerPage - 1) * loPickerPageSize, loPickerPage * loPickerPageSize)

  const [pastCmpPage, setPastCmpPage] = useState(1)
  const [pastCmpPageSize, setPastCmpPageSize] = useState(25)
  const pastCmpPaged = pastComparisons.slice((pastCmpPage - 1) * pastCmpPageSize, pastCmpPage * pastCmpPageSize)

  const fmtDate = (iso?: string) => {
    if (!iso) return '—'
    const s = iso.includes('+') || iso.endsWith('Z') ? iso : iso + 'Z'
    return formatInTimeZone(new Date(s), 'America/New_York', 'MMM d, yyyy h:mm a')
  }

  const toggleSelect = (aid: string, defaultLabel: string) => {
    setSelectedIds(prev => {
      if (prev.includes(aid)) {
        // Deselecting the requiring course clears the designation
        if (aid === requiringId) setRequiringId(null)
        return prev.filter(id => id !== aid)
      }
      if (!labels[aid]) {
        setLabels(l => ({ ...l, [aid]: defaultLabel }))
      }
      return [...prev, aid]
    })
  }

  const handleTogglePrerequisiteMode = (checked: boolean) => {
    setIsPrerequisite(checked)
    setRequiringId(null)
  }

  const cloSensors = useSensors(useSensor(PointerSensor))

  const handleCloDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setSelectedIds(prev => {
        const oldIndex = prev.indexOf(active.id as string)
        const newIndex = prev.indexOf(over.id as string)
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }

  const handleRunComparison = async () => {
    if (!selectedOrganizationId || !selectedWorkspaceId || !userId) return

    if (isPrerequisite) {
      // Need >= 2 selected and exactly one designated requiring course
      if (selectedIds.length < 2 || !requiringId || !selectedIds.includes(requiringId)) return

      setError(null)
      setComparisonResult(null)
      setIsProcessing(true)
      setJobStatus('PENDING')

      const prerequisiteCourses = selectedIds
        .filter(id => id !== requiringId)
        .map(id => ({ label: labels[id] || id, analysis_id: id }))
      const requiringCourse = { label: labels[requiringId] || requiringId, analysis_id: requiringId }

      try {
        const res = await documentsApi.runPrerequisiteCheck(
          prerequisiteCourses,
          requiringCourse,
          selectedOrganizationId,
          selectedWorkspaceId,
          userId,
          workspaceConfig?.default_llm_model
        )
        setCurrentJobId(res.job_id)
      } catch (e: any) {
        setError(e?.message || 'Failed to start prerequisite check job.')
        setIsProcessing(false)
        setJobStatus(null)
      }
      return
    }

    if (selectedIds.length < 2) return

    setError(null)
    setComparisonResult(null)
    setIsProcessing(true)
    setJobStatus('PENDING')

    const orderedLabels = selectedIds.map(id => labels[id] || id)

    try {
      const res = await documentsApi.runCLOComparison(
        selectedIds,
        orderedLabels,
        selectedOrganizationId,
        selectedWorkspaceId,
        userId,
        workspaceConfig?.default_llm_model,
        false
      )
      setCurrentJobId(res.job_id)
    } catch (e: any) {
      setError(e?.message || 'Failed to start CLO comparison job.')
      setIsProcessing(false)
      setJobStatus(null)
    }
  }

  const handleJobComplete = async () => {
    try {
      const analysisType = isPrerequisite ? 'prerequisite check' : 'clo comparison'
      const data = await analysisHistoryApi.listAnalyses(
        selectedOrganizationId!,
        selectedWorkspaceId!,
        10,
        'completed',
        analysisType
      )
      const newest = data.analyses
        .filter(a => {
          const ids = a.document_ids || []
          return selectedIds.length === ids.length && selectedIds.every(id => ids.includes(id))
        })
        .sort((a, b) => new Date(b.completed_at || 0).getTime() - new Date(a.completed_at || 0).getTime())[0]

      if (newest) {
        const detail = await analysisHistoryApi.getAnalysis(
          selectedOrganizationId!,
          selectedWorkspaceId!,
          newest.analysis_id,
          true
        )
        setComparisonResult(detail.analysis_data)
        void queryClient.invalidateQueries({ queryKey: ['clo-comparisons', selectedOrganizationId, selectedWorkspaceId] })
        void queryClient.invalidateQueries({ queryKey: ['prereq-checks', selectedOrganizationId, selectedWorkspaceId] })
      } else {
        setError('Comparison completed but result could not be retrieved.')
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to retrieve comparison results.')
    } finally {
      setCurrentJobId(null)
      setJobStatus(null)
      setIsProcessing(false)
    }
  }

  const handleJobError = () => {
    setError('CLO comparison failed. Please try again.')
    setCurrentJobId(null)
    setJobStatus(null)
    setIsProcessing(false)
  }

  const handleViewPast = async (a: AnalysisSummary) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) return
    setViewingComparisonId(a.analysis_id)
    setViewLoading(true)
    try {
      const detail = await analysisHistoryApi.getAnalysis(
        selectedOrganizationId,
        selectedWorkspaceId,
        a.analysis_id,
        true
      )
      setComparisonResult(detail.analysis_data)
    } catch (e: any) {
      setError(e?.message || 'Failed to load comparison.')
    } finally {
      setViewLoading(false)
      setViewingComparisonId(null)
    }
  }

  const handleDownloadPast = async (a: AnalysisSummary) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) return
    setDownloadingId(a.analysis_id)
    try {
      const detail = await analysisHistoryApi.getAnalysis(selectedOrganizationId, selectedWorkspaceId, a.analysis_id, true)
      if (!detail.analysis_data) { alert('No data available for download.'); return }
      const name = a.filenames?.join(' vs ') || a.analysis_id.slice(0, 8)
      await exportToAccessiblePDF(
        { status: 'completed', filename: name, document_type: 'clo_comparison', analyzedBy: a.created_by, analysisDate: a.completed_at, ...detail.analysis_data },
        { documentName: name }
      )
    } catch (e: any) {
      alert(e?.message || 'Failed to download PDF.')
    } finally {
      setDownloadingId(null)
    }
  }

  const handleDeletePast = (id: string) => {
    setAnalysisToDelete(id)
    setDeleteConfirmOpen(true)
  }

  const confirmDeletePast = async () => {
    if (!analysisToDelete || !selectedOrganizationId || !selectedWorkspaceId) return
    try {
      await analysisHistoryApi.deleteAnalysis(selectedOrganizationId, selectedWorkspaceId, analysisToDelete)
      void queryClient.invalidateQueries({ queryKey: ['clo-comparisons', selectedOrganizationId, selectedWorkspaceId] })
    } finally {
      setDeleteConfirmOpen(false)
      setAnalysisToDelete(null)
    }
  }

  const handleBack = () => {
    setComparisonResult(null)
    setSelectedIds([])
    setLabels({})
    setRequiringId(null)
  }

  // Derive prerequisite / requiring labels from result metadata for display
  const prereqLabel = comparisonResult?.document_metadata?.labels?.[0]
    || comparisonResult?.course_instances?.[0]?.label
    || 'Prerequisite Course'
  const requiringLabel = comparisonResult?.document_metadata?.labels?.[1]
    || comparisonResult?.course_instances?.[1]?.label
    || 'Requiring Course'

  // New multi-prerequisite readiness result (single-call prerequisite check)
  const isReadinessResult = Boolean(
    comparisonResult?.requiring_clo_readiness && comparisonResult?.readiness_rating
  )
  const readinessPrereqLabels: string[] =
    comparisonResult?.prerequisite_courses?.map((c: any) => c.label)
    || comparisonResult?.document_metadata?.labels?.slice(0, -1)
    || []
  const readinessRequiringLabel: string =
    comparisonResult?.requiring_course?.label
    || comparisonResult?.document_metadata?.labels?.slice(-1)?.[0]
    || 'Requiring Course'

  // Old-shape prerequisite result (legacy 2-course gap_analysis)
  const isPrerequisiteResult = Boolean(
    comparisonResult?.gap_analysis && comparisonResult?.readiness_rating
  )

  const canRun = isPrerequisite
    ? (selectedIds.length >= 2 && !!requiringId && selectedIds.includes(requiringId))
    : selectedIds.length >= 2

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {currentJobId && (
        <JobPoller
          jobId={currentJobId}
          jobContext={{
            org_id: selectedOrganizationId || undefined,
            ws_id: selectedWorkspaceId || undefined,
          }}
          onStatusChange={setJobStatus}
          onComplete={handleJobComplete}
          onError={handleJobError}
        />
      )}

      {/* Job status banner */}
      {isProcessing && (
        <JobStatus
          jobId={currentJobId}
          jobStatus={jobStatus}
          isProcessing={isProcessing}
          processDescription={
            isPrerequisite
              ? `Checking prerequisite readiness for ${(requiringId && labels[requiringId]) || 'requiring course'}...`
              : `Comparing CLOs across ${selectedIds.length} course instances...`
          }
        />
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <span className="text-red-500 text-lg">⚠️</span>
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Result view */}
      {comparisonResult && !isProcessing && (
        <div className="space-y-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Selection
          </button>

          {isReadinessResult ? (
            /* New multi-prerequisite readiness result */
            <>
              <div className="bg-white border border-gray-200 rounded-xl px-5 py-3 flex flex-wrap items-center gap-2">
                <span className="text-gray-600 font-semibold text-sm">Prerequisite Check</span>
                <span className="text-gray-300">·</span>
                {readinessPrereqLabels.map((lbl, i) => (
                  <span key={i} className="text-sm font-medium text-gray-700">
                    {lbl}{i < readinessPrereqLabels.length - 1 ? ',' : ''}
                  </span>
                ))}
                <span className="mx-1 text-gray-400">→</span>
                <span className="text-sm font-medium text-gray-700">{readinessRequiringLabel}</span>
              </div>

              <PrerequisiteReadiness
                requiring_clo_readiness={comparisonResult.requiring_clo_readiness}
                readiness_rating={comparisonResult.readiness_rating}
                readiness_explanation={comparisonResult.readiness_explanation || ''}
                summary={comparisonResult.summary}
                prerequisite_labels={readinessPrereqLabels}
                requiring_label={readinessRequiringLabel}
              />
            </>
          ) : (
            <>
              {/* Legacy prerequisite check result banner */}
              {isPrerequisiteResult && (
                <div className="bg-white border border-gray-200 rounded-xl px-5 py-3 flex items-center gap-3">
                  <span className="text-gray-600 font-semibold text-sm">Prerequisite Check</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">{prereqLabel}</span>
                    <span className="mx-1 text-gray-400">→</span>
                    <span className="font-medium">{requiringLabel}</span>
                  </span>
                </div>
              )}

              <CLOHeatmap
                canonical_clos={comparisonResult.canonical_clos || []}
                course_instances={comparisonResult.course_instances || []}
                summary={comparisonResult.summary}
              />

              {/* Legacy prerequisite gap analysis section */}
              {isPrerequisiteResult && (
                <PrerequisiteGapAnalysis
                  gap_analysis={comparisonResult.gap_analysis}
                  readiness_rating={comparisonResult.readiness_rating}
                  readiness_explanation={comparisonResult.readiness_explanation || ''}
                  prerequisite_label={prereqLabel}
                  requiring_label={requiringLabel}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Selection section — hidden while processing or viewing result */}
      {!comparisonResult && !isProcessing && (
        <>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-start justify-between mb-4 gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Select LO Analyses to Compare</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {isPrerequisite
                    ? 'Select one or more prerequisite courses (taken first) and one requiring course, then mark which is the requiring course.'
                    : 'Select 2 or more completed LO analyses to compare their CLOs.'}
                </p>
              </div>
              <button
                onClick={() => { if (!canRun || isProcessing) return; if (isPrerequisite) { handleRunComparison(); return } setSelectedIds(ids => [...ids].sort((a, b) => (labels[a] || a).localeCompare(labels[b] || b))); setShowOrderConfirm(true) }}
                disabled={!canRun || isProcessing}
                className="shrink-0 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center gap-2"
              >
                {isProcessing ? (
                  <><Loader2 className="animate-spin h-4 w-4" /> Running...</>
                ) : isPrerequisite ? (
                  <>Check Prerequisite Readiness</>
                ) : (
                  <>Compare CLOs ({selectedIds.length} selected)</>
                )}
              </button>
            </div>

            {/* Prerequisite mode toggle */}
            <div className="mb-4 flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <div
                  onClick={() => handleTogglePrerequisiteMode(!isPrerequisite)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                    isPrerequisite ? 'bg-indigo-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    isPrerequisite ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </div>
                <span className="text-sm font-medium text-gray-700">Prerequisite Check Mode</span>
              </label>
              <span className="text-xs text-gray-500">
                {isPrerequisite
                  ? 'Assess how one or more prerequisite courses prepare students for a requiring course.'
                  : 'Compare CLOs side-by-side across multiple course instances.'}
              </span>
            </div>

            {/* Info banner */}
            {!canRun && selectedIds.length > 0 && (
              <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
                {isPrerequisite
                  ? selectedIds.length < 2
                    ? 'Select at least one prerequisite course and one requiring course (2 or more total).'
                    : 'Mark which selected course is the requiring course.'
                  : 'Select at least one more analysis to run a comparison.'}
              </div>
            )}

            {/* Role assignment for prerequisite mode */}
            {isPrerequisite && selectedIds.length >= 1 && (
              <div className="mb-4 border border-gray-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Course Roles — select the requiring course; all others are prerequisites
                </p>
                {selectedIds.map(id => {
                  const isReq = id === requiringId
                  return (
                    <div key={id} className="flex items-center gap-3 mb-2">
                      <button
                        type="button"
                        onClick={() => setRequiringId(isReq ? null : id)}
                        className={`shrink-0 px-2 py-0.5 rounded text-xs font-bold transition-colors ${
                          isReq
                            ? 'bg-purple-100 text-purple-800 border border-purple-200'
                            : 'bg-gray-100 text-gray-600 border border-transparent hover:bg-gray-200'
                        }`}
                        title={isReq ? 'This is the requiring course' : 'Mark as the requiring course'}
                      >
                        {isReq ? 'REQUIRING' : 'PREREQUISITE'}
                      </button>
                      <input
                        type="text"
                        value={labels[id] || ''}
                        onChange={e => setLabels(l => ({ ...l, [id]: e.target.value }))}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Course label…"
                      />
                    </div>
                  )
                })}
              </div>
            )}

            {loLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
              </div>
            ) : cloAllSources.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No LO analyses found.</p>
                <p className="text-sm text-gray-500 mt-2">
                  Run an extraction from the "Extract from Documents" tab first.
                </p>
              </div>
            ) : (
              <>
              {/* Select all toggle */}
              <label className="flex items-center gap-2 mb-3 w-fit text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  checked={cloAllSources.length > 0 && selectedIds.length === cloAllSources.length}
                  ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < cloAllSources.length }}
                  onChange={() => {
                    if (selectedIds.length === cloAllSources.length) {
                      setSelectedIds([]); setRequiringId(null)
                    } else {
                      setSelectedIds(cloAllSources.map(s => s.id))
                      setLabels(prev => {
                        const next = { ...prev }
                        cloAllSources.forEach(s => { if (!next[s.id]) next[s.id] = s.filenames?.join(', ') || `Analysis ${s.id.slice(0, 8)}` })
                        return next
                      })
                    }
                  }}
                />
                {cloAllSources.length > 0 && selectedIds.length === cloAllSources.length ? 'Deselect all' : `Select all (${cloAllSources.length})`}
              </label>
              <PaginationTop total={cloAllSources.length} page={loPickerPage} pageSize={loPickerPageSize} label="analyses"
                onPageSizeChange={n => { setLoPickerPageSize(n); setLoPickerPage(1) }} />
              <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 w-12" />
                      {isPrerequisite && <th className="px-4 py-3 w-28 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>}
                      <th onClick={() => handleCloSort('type')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                        <div className="flex items-center gap-2">Type <CloSortIcon col="type" /></div>
                      </th>
                      <th onClick={() => handleCloSort('created')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                        <div className="flex items-center gap-2">Created <CloSortIcon col="created" /></div>
                      </th>
                      <th onClick={() => handleCloSort('documents')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                        <div className="flex items-center gap-2">Documents <CloSortIcon col="documents" /></div>
                      </th>
                      <th onClick={() => handleCloSort('completed')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                        <div className="flex items-center gap-2">Completed <CloSortIcon col="completed" /></div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loPickerPaged.map(a => {
                      const isSelected = selectedIds.includes(a.id)
                      const isReq = a.id === requiringId
                      const defaultLabel = a.filenames?.join(', ') || `Analysis ${a.id.slice(0, 8)}`
                      return (
                        <tr
                          key={a.id}
                          onClick={() => toggleSelect(a.id, defaultLabel)}
                          className={`transition-colors ${
                            isSelected ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                              isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </td>
                          {isPrerequisite && (
                            <td className="px-4 py-3">
                              {isSelected && (
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                  isReq ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {isReq ? 'REQUIRING' : 'PREREQ'}
                                </span>
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {a.sourceType === 'bulk-lo'
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Bulk LO</span>
                              : a.sourceType === 'bulk-analysis'
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700" title="Learning objectives reused from a main Analysis tab bulk run">Bulk Analysis History</span>
                                : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">LO</span>
                            }
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{fmtDate(a.created_at)}</td>
                          <td className="px-4 py-3">
                            {a.filenames && a.filenames.length > 0 ? (
                              <ul className="text-xs text-gray-600 space-y-0.5">
                                {a.filenames.map((f, i) => (
                                  <li key={i} className="truncate max-w-xs" title={f}>{f}</li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-sm text-gray-900">
                                {a.document_count} {a.document_count === 1 ? 'document' : 'documents'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{fmtDate(a.completed_at)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{a.created_by || '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <PaginationBottom total={cloAllSources.length} page={loPickerPage} pageSize={loPickerPageSize}
                onPrev={() => setLoPickerPage(p => Math.max(1, p - 1))}
                onNext={() => setLoPickerPage(p => Math.min(Math.ceil(cloAllSources.length / loPickerPageSize), p + 1))} />
              </>
            )}
          </div>

          {/* Past comparisons */}
          {!pastLoading && pastComparisons.length > 0 && (
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Past Comparisons</h2>
              <PaginationTop total={pastComparisons.length} page={pastCmpPage} pageSize={pastCmpPageSize} label="comparisons"
                onPageSizeChange={n => { setPastCmpPageSize(n); setPastCmpPage(1) }} />
              <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Instances</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pastCmpPaged.map(a => (
                      <tr key={a.analysis_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{fmtDate(a.created_at)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            a.analysis_type === 'prerequisite check'
                              ? 'bg-indigo-100 text-indigo-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {a.analysis_type === 'prerequisite check' ? 'Prerequisite Check' : 'CLO Comparison'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {a.filenames && a.filenames.length > 0 ? (
                            <ul className="text-xs text-gray-600 space-y-0.5">
                              {a.filenames.map((f, i) => (
                                <li key={i} className="truncate max-w-xs" title={f}>{f}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-sm text-gray-900">{a.document_count} instances</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {a.llm_cost ? `$${parseFloat(a.llm_cost).toFixed(4)}` : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{a.created_by || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap flex items-center gap-1">
                          <button
                            onClick={() => handleViewPast(a)}
                            disabled={viewLoading && viewingComparisonId === a.analysis_id}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                            title="View result"
                          >
                            {viewLoading && viewingComparisonId === a.analysis_id
                              ? <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                              : <Eye className="h-4 w-4" />
                            }
                          </button>
                          <button
                            onClick={() => handleDownloadPast(a)}
                            disabled={downloadingId === a.analysis_id}
                            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                            title="Download PDF"
                          >
                            {downloadingId === a.analysis_id
                              ? <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full" />
                              : <Download className="h-4 w-4" />
                            }
                          </button>
                          <button
                            onClick={() => handleDeletePast(a.analysis_id)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBottom total={pastComparisons.length} page={pastCmpPage} pageSize={pastCmpPageSize}
                onPrev={() => setPastCmpPage(p => Math.max(1, p - 1))}
                onNext={() => setPastCmpPage(p => Math.min(Math.ceil(pastComparisons.length / pastCmpPageSize), p + 1))} />
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setAnalysisToDelete(null) }}
        onConfirm={confirmDeletePast}
        title="Delete Analysis"
        message="Are you sure you want to delete this analysis? This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Order confirmation modal */}
      {showOrderConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 p-6 space-y-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Confirm Column Order</h3>
              <p className="text-sm text-gray-500 mt-1">
                This will be the left-to-right column order in the heatmap. Drag to reorder before running.
              </p>
            </div>
            <DndContext sensors={cloSensors} collisionDetection={closestCenter} onDragEnd={handleCloDragEnd}>
              <SortableContext items={selectedIds} strategy={verticalListSortingStrategy}>
                <ol className="space-y-2 max-h-[60vh] overflow-y-auto">
                  {selectedIds.map((id, idx) => (
                    <SortableSourceItem
                      key={id}
                      id={id}
                      label={labels[id] ?? id.slice(0, 8)}
                      idx={idx}
                      onRename={v => setLabels(l => ({ ...l, [id]: v }))}
                    />
                  ))}
                </ol>
              </SortableContext>
            </DndContext>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowOrderConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowOrderConfirm(false); handleRunComparison() }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Confirm &amp; Compare
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── PLO Generation Tab ───────────────────────────────────────────────────────
function SortableSourceItem({ id, label, idx, onRename }: { id: string; label: string; idx: number; onRename?: (value: string) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const [editing, setEditing] = useState(false)
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }
  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-200 cursor-grab active:cursor-grabbing touch-none"
    >
      <GripVertical size={16} className="shrink-0 text-gray-400" />
      <span className="text-xs font-bold text-blue-500 w-5 shrink-0">{idx + 1}.</span>
      {onRename && editing ? (
        <input
          type="text"
          autoFocus
          value={label}
          onChange={e => onRename(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(false) }}
          onBlur={() => setEditing(false)}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          className="text-sm text-gray-800 flex-1 min-w-0 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-text touch-auto"
          placeholder="Column label…"
        />
      ) : (
        <>
          <span className="text-sm text-gray-800 flex-1 break-words min-w-0">{label}</span>
          {onRename && (
            <button
              type="button"
              onPointerDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); setEditing(true) }}
              className="shrink-0 p-1 text-gray-400 hover:text-blue-600 hover:bg-gray-100 rounded transition-colors cursor-pointer"
              title="Rename column"
              aria-label="Rename column"
            >
              <Pencil size={14} />
            </button>
          )}
        </>
      )}
    </li>
  )
}

function PLOTab() {
  const { selectedOrganizationId, selectedWorkspaceId, userId } = useAuth()

  // Selection: multi-select CLO comparisons
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [showOrderConfirm, setShowOrderConfirm] = useState(false)
  const [labelOverrides, setLabelOverrides] = useState<Record<string, string>>({})

  // Job tracking
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Result
  const [ploResult, setPloResult] = useState<any>(null)

  // View past PLO generation
  const [viewingPloId, setViewingPloId] = useState<string | null>(null)
  const [viewLoading, setViewLoading] = useState(false)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [analysisToDelete, setAnalysisToDelete] = useState<string | null>(null)
  const [ploSortCol, setPloSortCol] = useState<'type' | 'created' | 'documents' | 'completed'>('created')
  const [ploSortDir, setPloSortDir] = useState<'asc' | 'desc'>('desc')

  const queryClient = useQueryClient()

  const { data: workspaceConfig } = useQuery({
    queryKey: ['workspaceConfig', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => configApi.getWorkspaceConfig(selectedOrganizationId!, selectedWorkspaceId!),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Bulk LO analyses available for selection (document_count > 1)
  const { data: bulkLoData, isLoading: bulkLoLoading } = useQuery({
    queryKey: ['bulk-lo-for-plo', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', 'learning objectives'),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Individual documents with a completed LO extraction (same cache key as parent)
  const { data: allDocuments = [], isLoading: docsLoading } = useQuery<Document[]>({
    queryKey: ['documents', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => documentsApi.getDocuments(selectedOrganizationId!, selectedWorkspaceId!),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Bulk analyses from the main Analysis tab — their LO section is reusable here (document_count > 1)
  const { data: ploBulkAnalysesData } = useQuery({
    queryKey: ['bulk-analyses-for-plo', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', 'bulk analyzer'),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Past PLO generations
  const { data: pastPloData, isLoading: pastPloLoading } = useQuery({
    queryKey: ['plo-generations', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', 'plo generation'),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  const byDateDesc = (a: { created_at?: string }, b: { created_at?: string }) =>
    new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()

  // Unified source type: analysis history items + individual documents with LO data
  type PloSource = { id: string; sourceType: 'bulk-lo' | 'lo' | 'bulk-analysis'; created_at?: string; completed_at?: string; filenames?: string[]; document_count?: number; folder?: string; created_by?: string }

  const bulkLoAnalyses = (bulkLoData?.analyses ?? []).filter(a => a.document_count > 1).sort(byDateDesc)
  const docsWithLO = allDocuments.filter(d => d.learningobjectives_data)
  const ploBulkAnalyses = (ploBulkAnalysesData?.analyses ?? []).filter(a => (a.document_count ?? 0) > 1).sort(byDateDesc)

  const allSources: PloSource[] = [
    ...bulkLoAnalyses.map(a => ({ id: a.analysis_id, sourceType: 'bulk-lo' as const, created_at: a.created_at, completed_at: a.completed_at, filenames: a.filenames, document_count: a.document_count, created_by: a.created_by })),
    ...ploBulkAnalyses.map(a => ({ id: a.analysis_id, sourceType: 'bulk-analysis' as const, created_at: a.created_at, completed_at: a.completed_at, filenames: a.filenames, document_count: a.document_count, created_by: a.created_by })),
    ...docsWithLO.map(d => ({ id: d.document_id, sourceType: 'lo' as const, created_at: d.uploaded_at, completed_at: d.learningobjectives_data?.document_metadata?.analysis_timestamp, filenames: [d.original_filename], document_count: 1, folder: d.folder, created_by: d.uploaded_by_username || d.uploaded_by })),
  ].sort(byDateDesc)
  const pastPloGenerations: AnalysisSummary[] = (pastPloData?.analyses ?? []).sort((a: AnalysisSummary, b: AnalysisSummary) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())

  const sortedPloSources = useMemo(() => [...allSources].sort((a, b) => {
    let av: any, bv: any
    if (ploSortCol === 'type') { av = a.sourceType; bv = b.sourceType }
    else if (ploSortCol === 'created') { av = new Date(a.created_at || 0).getTime(); bv = new Date(b.created_at || 0).getTime() }
    else if (ploSortCol === 'documents') { av = (a.filenames?.[0] || '').toLowerCase(); bv = (b.filenames?.[0] || '').toLowerCase() }
    else { av = new Date(a.completed_at || 0).getTime(); bv = new Date(b.completed_at || 0).getTime() }
    if (av < bv) return ploSortDir === 'asc' ? -1 : 1
    if (av > bv) return ploSortDir === 'asc' ? 1 : -1
    return 0
  }), [allSources, ploSortCol, ploSortDir])

  const [ploSourcePage, setPloSourcePage] = useState(1)
  const [ploSourcePageSize, setPloSourcePageSize] = useState(25)
  const ploSourcePaged = sortedPloSources.slice((ploSourcePage - 1) * ploSourcePageSize, ploSourcePage * ploSourcePageSize)

  const [ploPastPage, setPloPastPage] = useState(1)
  const [ploPastPageSize, setPloPastPageSize] = useState(25)
  const ploPastPaged = pastPloGenerations.slice((ploPastPage - 1) * ploPastPageSize, ploPastPage * ploPastPageSize)

  const fmtDate = (iso?: string) => {
    if (!iso) return '—'
    const s = iso.includes('+') || iso.endsWith('Z') ? iso : iso + 'Z'
    return formatInTimeZone(new Date(s), 'America/New_York', 'MMM d, yyyy h:mm a')
  }

  const handlePloSort = (col: 'type' | 'created' | 'documents' | 'completed') => {
    if (ploSortCol === col) {
      setPloSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setPloSortCol(col)
      setPloSortDir('asc')
    }
    setPloSourcePage(1)
  }

  const PloSortIcon = ({ col }: { col: 'type' | 'created' | 'documents' | 'completed' }) => {
    if (ploSortCol !== col) return <span className="text-blue-600">⇅</span>
    return <span className="text-blue-600">{ploSortDir === 'asc' ? '↑' : '↓'}</span>
  }

  const toggleSelect = (aid: string) => {
    setSelectedIds(prev =>
      prev.includes(aid) ? prev.filter(id => id !== aid) : [...prev, aid]
    )
  }

  const moveUp = (id: string) => {
    setSelectedIds(prev => {
      const i = prev.indexOf(id)
      if (i <= 0) return prev
      const next = [...prev]
      ;[next[i - 1], next[i]] = [next[i], next[i - 1]]
      return next
    })
  }

  const moveDown = (id: string) => {
    setSelectedIds(prev => {
      const i = prev.indexOf(id)
      if (i < 0 || i >= prev.length - 1) return prev
      const next = [...prev]
      ;[next[i], next[i + 1]] = [next[i + 1], next[i]]
      return next
    })
  }

  const ploSensors = useSensors(useSensor(PointerSensor))
  const handlePloDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setSelectedIds(prev => {
        const oldIndex = prev.indexOf(active.id as string)
        const newIndex = prev.indexOf(over.id as string)
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }

  const handleRunPLOGeneration = async () => {
    if (!selectedOrganizationId || !selectedWorkspaceId || !userId) return
    if (selectedIds.length < 2) return

    setError(null)
    setPloResult(null)
    setIsProcessing(true)
    setJobStatus('PENDING')

    const labels = selectedIds.map(id => {
      const a = allSources.find(x => x.id === id)
      const def = a?.filenames?.join(', ') || id.slice(0, 8)
      return labelOverrides[id]?.trim() || def
    })

    try {
      const res = await documentsApi.runPLOGeneration(
        selectedIds,
        labels,
        selectedOrganizationId,
        selectedWorkspaceId,
        userId,
        workspaceConfig?.default_llm_model
      )
      setCurrentJobId(res.job_id)
    } catch (e: any) {
      setError(e?.message || 'Failed to start PLO generation job.')
      setIsProcessing(false)
      setJobStatus(null)
    }
  }

  const handleJobComplete = async () => {
    try {
      const data = await analysisHistoryApi.listAnalyses(
        selectedOrganizationId!,
        selectedWorkspaceId!,
        10,
        'completed',
        'plo generation'
      )
      const newest = data.analyses
        .filter(a => {
          const ids = a.document_ids || []
          return selectedIds.length === ids.length && selectedIds.every(id => ids.includes(id))
        })
        .sort((a, b) => new Date(b.completed_at || 0).getTime() - new Date(a.completed_at || 0).getTime())[0]

      if (newest) {
        const detail = await analysisHistoryApi.getAnalysis(
          selectedOrganizationId!,
          selectedWorkspaceId!,
          newest.analysis_id,
          true
        )
        setPloResult(detail.analysis_data)
        void queryClient.invalidateQueries({ queryKey: ['plo-generations', selectedOrganizationId, selectedWorkspaceId] })
      } else {
        setError('PLO generation completed but result could not be retrieved.')
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to retrieve PLO generation results.')
    } finally {
      setCurrentJobId(null)
      setJobStatus(null)
      setIsProcessing(false)
    }
  }

  const handleJobError = () => {
    setError('PLO generation failed. Please try again.')
    setCurrentJobId(null)
    setJobStatus(null)
    setIsProcessing(false)
  }

  const handleViewPast = async (a: AnalysisSummary) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) return
    setViewingPloId(a.analysis_id)
    setViewLoading(true)
    try {
      const detail = await analysisHistoryApi.getAnalysis(
        selectedOrganizationId,
        selectedWorkspaceId,
        a.analysis_id,
        true
      )
      setPloResult(detail.analysis_data)
    } catch (e: any) {
      setError(e?.message || 'Failed to load PLO generation result.')
    } finally {
      setViewLoading(false)
      setViewingPloId(null)
    }
  }

  const handleDownloadPast = async (a: AnalysisSummary) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) return
    setDownloadingId(a.analysis_id)
    try {
      const detail = await analysisHistoryApi.getAnalysis(selectedOrganizationId, selectedWorkspaceId, a.analysis_id, true)
      if (!detail.analysis_data) { alert('No data available for download.'); return }
      const name = a.filenames?.join(', ') || a.analysis_id.slice(0, 8)
      await exportToAccessiblePDF(
        { status: 'completed', filename: name, document_type: 'plo_generation', analyzedBy: a.created_by, analysisDate: a.completed_at, ...detail.analysis_data },
        { documentName: name }
      )
    } catch (e: any) {
      alert(e?.message || 'Failed to download PDF.')
    } finally {
      setDownloadingId(null)
    }
  }

  const handleDeletePast = (id: string) => {
    setAnalysisToDelete(id)
    setDeleteConfirmOpen(true)
  }

  const confirmDeletePast = async () => {
    if (!analysisToDelete || !selectedOrganizationId || !selectedWorkspaceId) return
    try {
      await analysisHistoryApi.deleteAnalysis(selectedOrganizationId, selectedWorkspaceId, analysisToDelete)
      void queryClient.invalidateQueries({ queryKey: ['plo-generations', selectedOrganizationId, selectedWorkspaceId] })
    } finally {
      setDeleteConfirmOpen(false)
      setAnalysisToDelete(null)
    }
  }

  const handleBack = () => {
    setPloResult(null)
    setSelectedIds([])
  }

  const canRun = selectedIds.length >= 2

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {currentJobId && (
        <JobPoller
          jobId={currentJobId}
          jobContext={{
            org_id: selectedOrganizationId || undefined,
            ws_id: selectedWorkspaceId || undefined,
          }}
          onStatusChange={setJobStatus}
          onComplete={handleJobComplete}
          onError={handleJobError}
        />
      )}

      {/* Job status banner */}
      {isProcessing && (
        <JobStatus
          jobId={currentJobId}
          jobStatus={jobStatus}
          isProcessing={isProcessing}
          processDescription={`Generating PLOs from ${selectedIds.length} CLO comparison${selectedIds.length !== 1 ? 's' : ''}...`}
        />
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <span className="text-red-500 text-lg">⚠️</span>
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Result view */}
      {ploResult && !isProcessing && (
        <div className="space-y-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Selection
          </button>
          <PLOHeatmap
            program_learning_outcomes={ploResult.program_learning_outcomes || []}
            course_instances={ploResult.course_instances || []}
            summary={ploResult.summary}
          />
        </div>
      )}

      {/* Selection section — hidden while processing or viewing result */}
      {!ploResult && !isProcessing && (
        <>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-start justify-between mb-4 gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Select CLO Comparisons</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Select two or more completed CLO comparisons to synthesize into Program Learning Outcomes.
                </p>
              </div>
              <button
                onClick={() => { if (!canRun || isProcessing) return; setSelectedIds(ids => [...ids].sort((a, b) => { const la = allSources.find(x => x.id === a)?.filenames?.join(', ') || a; const lb = allSources.find(x => x.id === b)?.filenames?.join(', ') || b; return la.localeCompare(lb) })); setShowOrderConfirm(true) }}
                disabled={!canRun || isProcessing}
                className="shrink-0 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center gap-2"
              >
                {isProcessing ? (
                  <><Loader2 className="animate-spin h-4 w-4" /> Running...</>
                ) : (
                  <>Generate PLOs ({selectedIds.length} selected)</>
                )}
              </button>
            </div>

            {bulkLoLoading || docsLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
              </div>
            ) : allSources.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No sources found.</p>
                <p className="text-sm text-gray-500 mt-2">
                  Run a CLO comparison or a bulk LO analysis first.
                </p>
              </div>
            ) : (
              <>
              {/* Select all toggle */}
              <label className="flex items-center gap-2 mb-3 w-fit text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  checked={sortedPloSources.length > 0 && selectedIds.length === sortedPloSources.length}
                  ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < sortedPloSources.length }}
                  onChange={() => setSelectedIds(
                    selectedIds.length === sortedPloSources.length ? [] : sortedPloSources.map(s => s.id)
                  )}
                />
                {sortedPloSources.length > 0 && selectedIds.length === sortedPloSources.length ? 'Deselect all' : `Select all (${sortedPloSources.length})`}
              </label>
              <PaginationTop total={sortedPloSources.length} page={ploSourcePage} pageSize={ploSourcePageSize} label="sources"
                onPageSizeChange={n => { setPloSourcePageSize(n); setPloSourcePage(1) }} />
              <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 w-12" />
                      <th onClick={() => handlePloSort('type')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                        <div className="flex items-center gap-2">Type <PloSortIcon col="type" /></div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Folder</th>
                      <th onClick={() => handlePloSort('created')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                        <div className="flex items-center gap-2">Created <PloSortIcon col="created" /></div>
                      </th>
                      <th onClick={() => handlePloSort('documents')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                        <div className="flex items-center gap-2">Files <PloSortIcon col="documents" /></div>
                      </th>
                      <th onClick={() => handlePloSort('completed')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                        <div className="flex items-center gap-2">Completed <PloSortIcon col="completed" /></div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {ploSourcePaged.map(a => {
                      const isSelected = selectedIds.includes(a.id)
                      return (
                        <tr
                          key={a.id}
                          onClick={() => toggleSelect(a.id)}
                          className={`transition-colors cursor-pointer ${
                            isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                              isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {a.sourceType === 'bulk-lo'
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Bulk LO</span>
                              : a.sourceType === 'bulk-analysis'
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700" title="Learning objectives reused from a main Analysis tab bulk run">Bulk Analysis History</span>
                                : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">LO</span>
                            }
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {a.sourceType === 'lo' && a.folder ? <span style={{ textTransform: 'uppercase' }}>{a.folder}</span> : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{fmtDate(a.created_at)}</td>
                          <td className="px-4 py-3">
                            {a.filenames && a.filenames.length > 0 ? (
                              <ul className="text-xs text-gray-600 space-y-0.5">
                                {a.filenames.map((f, i) => (
                                  <li key={i} className="truncate max-w-xs" title={f}>{f}</li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-sm text-gray-900">{a.document_count} files</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{fmtDate(a.completed_at)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{a.created_by || '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <PaginationBottom total={sortedPloSources.length} page={ploSourcePage} pageSize={ploSourcePageSize}
                onPrev={() => setPloSourcePage(p => Math.max(1, p - 1))}
                onNext={() => setPloSourcePage(p => Math.min(Math.ceil(sortedPloSources.length / ploSourcePageSize), p + 1))} />
              </>
            )}

          </div>

          {/* Order confirmation modal */}
          {showOrderConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Confirm Column Order</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This will be the left-to-right column order in the heatmap. Drag to reorder before generating.
                  </p>
                </div>
                <DndContext sensors={ploSensors} collisionDetection={closestCenter} onDragEnd={handlePloDragEnd}>
                  <SortableContext items={selectedIds} strategy={verticalListSortingStrategy}>
                    <ol className="space-y-2 max-h-[60vh] overflow-y-auto">
                      {selectedIds.map((id, idx) => {
                        const src = allSources.find(x => x.id === id)
                        const def = src?.filenames?.join(', ') || id.slice(0, 8)
                        return (
                          <SortableSourceItem
                            key={id}
                            id={id}
                            label={labelOverrides[id] ?? def}
                            idx={idx}
                            onRename={v => setLabelOverrides(o => ({ ...o, [id]: v }))}
                          />
                        )
                      })}
                    </ol>
                  </SortableContext>
                </DndContext>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setShowOrderConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => { setShowOrderConfirm(false); handleRunPLOGeneration() }}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Confirm &amp; Generate
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Past PLO generations */}
          {!pastPloLoading && pastPloGenerations.length > 0 && (
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Past PLO Generations</h2>
              <PaginationTop total={pastPloGenerations.length} page={ploPastPage} pageSize={ploPastPageSize} label="generations"
                onPageSizeChange={n => { setPloPastPageSize(n); setPloPastPage(1) }} />
              <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comparisons Used</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {ploPastPaged.map(a => (
                      <tr key={a.analysis_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{fmtDate(a.created_at)}</td>
                        <td className="px-4 py-3">
                          {a.filenames && a.filenames.length > 0 ? (
                            <ul className="text-xs text-gray-600 space-y-0.5">
                              {a.filenames.map((f, i) => (
                                <li key={i} className="truncate max-w-xs" title={f}>{f}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-sm text-gray-900">{a.document_count} comparison(s)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {a.llm_cost ? `$${parseFloat(a.llm_cost).toFixed(4)}` : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{a.created_by || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap flex items-center gap-1">
                          <button
                            onClick={() => handleViewPast(a)}
                            disabled={viewLoading && viewingPloId === a.analysis_id}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                            title="View PLO heatmap"
                          >
                            {viewLoading && viewingPloId === a.analysis_id
                              ? <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                              : <Eye className="h-4 w-4" />
                            }
                          </button>
                          <button
                            onClick={() => handleDownloadPast(a)}
                            disabled={downloadingId === a.analysis_id}
                            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                            title="Download PDF"
                          >
                            {downloadingId === a.analysis_id
                              ? <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full" />
                              : <Download className="h-4 w-4" />
                            }
                          </button>
                          <button
                            onClick={() => handleDeletePast(a.analysis_id)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBottom total={pastPloGenerations.length} page={ploPastPage} pageSize={ploPastPageSize}
                onPrev={() => setPloPastPage(p => Math.max(1, p - 1))}
                onNext={() => setPloPastPage(p => Math.min(Math.ceil(pastPloGenerations.length / ploPastPageSize), p + 1))} />
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setAnalysisToDelete(null) }}
        onConfirm={confirmDeletePast}
        title="Delete Analysis"
        message="Are you sure you want to delete this analysis? This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  )
}

// ─── PLO Mapping Tab (Custom PLOs via CSV) ────────────────────────────────────
function PLOMappingTab() {
  const { selectedOrganizationId, selectedWorkspaceId, userId } = useAuth()

  // CLO comparison selection
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [showOrderConfirm, setShowOrderConfirm] = useState(false)
  const [labelOverrides, setLabelOverrides] = useState<Record<string, string>>({})

  // DOCX file upload
  const [ploDocxFile, setPloDocxFile] = useState<File | null>(null)
  const [ploFileBase64, setPloFileBase64] = useState<string>('')

  // Job tracking
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Result
  const [ploResult, setPloResult] = useState<any>(null)

  // View past PLO mappings
  const [viewingPloId, setViewingPloId] = useState<string | null>(null)
  const [viewLoading, setViewLoading] = useState(false)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [analysisToDelete, setAnalysisToDelete] = useState<string | null>(null)
  const [mapSortCol, setMapSortCol] = useState<'type' | 'created' | 'documents' | 'completed'>('created')
  const [mapSortDir, setMapSortDir] = useState<'asc' | 'desc'>('desc')

  const queryClient = useQueryClient()

  const { data: workspaceConfig } = useQuery({
    queryKey: ['workspaceConfig', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => configApi.getWorkspaceConfig(selectedOrganizationId!, selectedWorkspaceId!),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  const { data: bulkLoData, isLoading: bulkLoLoading } = useQuery({
    queryKey: ['bulk-lo-for-plo-mapping', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', 'learning objectives'),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  const { data: allDocuments = [], isLoading: docsLoading } = useQuery<Document[]>({
    queryKey: ['documents', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => documentsApi.getDocuments(selectedOrganizationId!, selectedWorkspaceId!),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  // Bulk analyses from the main Analysis tab — their LO section is reusable here (document_count > 1)
  const { data: mapBulkAnalysesData } = useQuery({
    queryKey: ['bulk-analyses-for-plo-mapping', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', 'bulk analyzer'),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  const { data: pastPloMappingsData, isLoading: pastPloLoading } = useQuery({
    queryKey: ['plo-mappings', selectedOrganizationId, selectedWorkspaceId],
    queryFn: () =>
      analysisHistoryApi.listAnalyses(selectedOrganizationId!, selectedWorkspaceId!, 100, 'completed', 'plo mapping'),
    enabled: Boolean(selectedOrganizationId && selectedWorkspaceId),
  })

  const byDateDesc = (a: { created_at?: string }, b: { created_at?: string }) =>
    new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()

  type PloSource = { id: string; sourceType: 'bulk-lo' | 'lo' | 'bulk-analysis'; created_at?: string; completed_at?: string; filenames?: string[]; document_count?: number; folder?: string; created_by?: string }

  const bulkLoAnalyses = (bulkLoData?.analyses ?? []).filter(a => a.document_count > 1).sort(byDateDesc)
  const docsWithLO = allDocuments.filter(d => d.learningobjectives_data)
  const mapBulkAnalyses = (mapBulkAnalysesData?.analyses ?? []).filter(a => (a.document_count ?? 0) > 1).sort(byDateDesc)

  const allSources: PloSource[] = [
    ...bulkLoAnalyses.map(a => ({ id: a.analysis_id, sourceType: 'bulk-lo' as const, created_at: a.created_at, completed_at: a.completed_at, filenames: a.filenames, document_count: a.document_count, created_by: a.created_by })),
    ...mapBulkAnalyses.map(a => ({ id: a.analysis_id, sourceType: 'bulk-analysis' as const, created_at: a.created_at, completed_at: a.completed_at, filenames: a.filenames, document_count: a.document_count, created_by: a.created_by })),
    ...docsWithLO.map(d => ({ id: d.document_id, sourceType: 'lo' as const, created_at: d.uploaded_at, completed_at: d.learningobjectives_data?.document_metadata?.analysis_timestamp, filenames: [d.original_filename], document_count: 1, folder: d.folder, created_by: d.uploaded_by_username || d.uploaded_by })),
  ].sort(byDateDesc)
  const pastPloMappings: AnalysisSummary[] = (pastPloMappingsData?.analyses ?? []).sort((a: AnalysisSummary, b: AnalysisSummary) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())

  const sortedMapSources = useMemo(() => [...allSources].sort((a, b) => {
    let av: any, bv: any
    if (mapSortCol === 'type') { av = a.sourceType; bv = b.sourceType }
    else if (mapSortCol === 'created') { av = new Date(a.created_at || 0).getTime(); bv = new Date(b.created_at || 0).getTime() }
    else if (mapSortCol === 'documents') { av = (a.filenames?.[0] || '').toLowerCase(); bv = (b.filenames?.[0] || '').toLowerCase() }
    else { av = new Date(a.completed_at || 0).getTime(); bv = new Date(b.completed_at || 0).getTime() }
    if (av < bv) return mapSortDir === 'asc' ? -1 : 1
    if (av > bv) return mapSortDir === 'asc' ? 1 : -1
    return 0
  }), [allSources, mapSortCol, mapSortDir])

  const [mapSourcePage, setMapSourcePage] = useState(1)
  const [mapSourcePageSize, setMapSourcePageSize] = useState(25)
  const mapSourcePaged = sortedMapSources.slice((mapSourcePage - 1) * mapSourcePageSize, mapSourcePage * mapSourcePageSize)

  const [mapPastPage, setMapPastPage] = useState(1)
  const [mapPastPageSize, setMapPastPageSize] = useState(25)
  const mapPastPaged = pastPloMappings.slice((mapPastPage - 1) * mapPastPageSize, mapPastPage * mapPastPageSize)

  const fmtDate = (iso?: string) => {
    if (!iso) return '—'
    const s = iso.includes('+') || iso.endsWith('Z') ? iso : iso + 'Z'
    return formatInTimeZone(new Date(s), 'America/New_York', 'MMM d, yyyy h:mm a')
  }

  const handleMapSort = (col: 'type' | 'created' | 'documents' | 'completed') => {
    if (mapSortCol === col) {
      setMapSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setMapSortCol(col)
      setMapSortDir('asc')
    }
    setMapSourcePage(1)
  }

  const MapSortIcon = ({ col }: { col: 'type' | 'created' | 'documents' | 'completed' }) => {
    if (mapSortCol !== col) return <span className="text-blue-600">⇅</span>
    return <span className="text-blue-600">{mapSortDir === 'asc' ? '↑' : '↓'}</span>
  }

  const toggleSelect = (aid: string) => {
    setSelectedIds(prev =>
      prev.includes(aid) ? prev.filter(id => id !== aid) : [...prev, aid]
    )
  }

  const moveUp = (id: string) => {
    setSelectedIds(prev => {
      const i = prev.indexOf(id)
      if (i <= 0) return prev
      const next = [...prev]
      ;[next[i - 1], next[i]] = [next[i], next[i - 1]]
      return next
    })
  }

  const moveDown = (id: string) => {
    setSelectedIds(prev => {
      const i = prev.indexOf(id)
      if (i < 0 || i >= prev.length - 1) return prev
      const next = [...prev]
      ;[next[i], next[i + 1]] = [next[i + 1], next[i]]
      return next
    })
  }

  const mapSensors = useSensors(useSensor(PointerSensor))
  const handleMapDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setSelectedIds(prev => {
        const oldIndex = prev.indexOf(active.id as string)
        const newIndex = prev.indexOf(over.id as string)
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPloDocxFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => {
      const arrayBuffer = ev.target?.result as ArrayBuffer
      const bytes = new Uint8Array(arrayBuffer)
      let binary = ''
      for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
      setPloFileBase64(btoa(binary))
    }
    reader.readAsArrayBuffer(file)
  }

  const handleRunPLOMapping = async () => {
    if (!selectedOrganizationId || !selectedWorkspaceId || !userId) return
    if (selectedIds.length < 2 || !ploFileBase64) return

    setError(null)
    setPloResult(null)
    setIsProcessing(true)
    setJobStatus('PENDING')

    const labels = selectedIds.map(id => {
      const a = allSources.find(x => x.id === id)
      const def = a?.filenames?.join(', ') || id.slice(0, 8)
      return labelOverrides[id]?.trim() || def
    })

    try {
      const res = await documentsApi.runPLOMapping(
        selectedIds,
        labels,
        ploFileBase64,
        selectedOrganizationId,
        selectedWorkspaceId,
        userId,
        workspaceConfig?.default_llm_model,
        ploDocxFile?.name
      )
      setCurrentJobId(res.job_id)
    } catch (e: any) {
      setError(e?.message || 'Failed to start PLO mapping job.')
      setIsProcessing(false)
      setJobStatus(null)
    }
  }

  const handleJobComplete = async () => {
    try {
      const data = await analysisHistoryApi.listAnalyses(
        selectedOrganizationId!,
        selectedWorkspaceId!,
        10,
        'completed',
        'plo mapping'
      )
      const newest = data.analyses
        .filter(a => {
          const ids = a.document_ids || []
          return selectedIds.length === ids.length && selectedIds.every(id => ids.includes(id))
        })
        .sort((a, b) => new Date(b.completed_at || 0).getTime() - new Date(a.completed_at || 0).getTime())[0]

      if (newest) {
        const detail = await analysisHistoryApi.getAnalysis(
          selectedOrganizationId!,
          selectedWorkspaceId!,
          newest.analysis_id,
          true
        )
        setPloResult(detail.analysis_data)
        void queryClient.invalidateQueries({ queryKey: ['plo-mappings', selectedOrganizationId, selectedWorkspaceId] })
      } else {
        setError('PLO mapping completed but result could not be retrieved.')
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to retrieve PLO mapping results.')
    } finally {
      setCurrentJobId(null)
      setJobStatus(null)
      setIsProcessing(false)
    }
  }

  const handleJobError = () => {
    setError('PLO mapping failed. Please try again.')
    setCurrentJobId(null)
    setJobStatus(null)
    setIsProcessing(false)
  }

  const handleViewPast = async (a: AnalysisSummary) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) return
    setViewingPloId(a.analysis_id)
    setViewLoading(true)
    try {
      const detail = await analysisHistoryApi.getAnalysis(
        selectedOrganizationId,
        selectedWorkspaceId,
        a.analysis_id,
        true
      )
      setPloResult(detail.analysis_data)
    } catch (e: any) {
      setError(e?.message || 'Failed to load PLO mapping result.')
    } finally {
      setViewLoading(false)
      setViewingPloId(null)
    }
  }

  const handleDownloadPast = async (a: AnalysisSummary) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) return
    setDownloadingId(a.analysis_id)
    try {
      const detail = await analysisHistoryApi.getAnalysis(selectedOrganizationId, selectedWorkspaceId, a.analysis_id, true)
      if (!detail.analysis_data) { alert('No data available for download.'); return }
      const name = a.filenames?.join(', ') || a.analysis_id.slice(0, 8)
      await exportToAccessiblePDF(
        { status: 'completed', filename: name, document_type: 'plo_mapping', analyzedBy: a.created_by, analysisDate: a.completed_at, ...detail.analysis_data },
        { documentName: name }
      )
    } catch (e: any) {
      alert(e?.message || 'Failed to download PDF.')
    } finally {
      setDownloadingId(null)
    }
  }

  const handleDeletePast = (id: string) => {
    setAnalysisToDelete(id)
    setDeleteConfirmOpen(true)
  }

  const confirmDeletePast = async () => {
    if (!analysisToDelete || !selectedOrganizationId || !selectedWorkspaceId) return
    try {
      await analysisHistoryApi.deleteAnalysis(selectedOrganizationId, selectedWorkspaceId, analysisToDelete)
      void queryClient.invalidateQueries({ queryKey: ['plo-mappings', selectedOrganizationId, selectedWorkspaceId] })
    } finally {
      setDeleteConfirmOpen(false)
      setAnalysisToDelete(null)
    }
  }

  const handleBack = () => {
    setPloResult(null)
    setSelectedIds([])
    setPloDocxFile(null)
    setPloFileBase64('')
  }

  const canRun = selectedIds.length >= 2 && ploFileBase64.length > 0

  return (
    <div className="space-y-6">
      {currentJobId && (
        <JobPoller
          jobId={currentJobId}
          jobContext={{
            org_id: selectedOrganizationId || undefined,
            ws_id: selectedWorkspaceId || undefined,
          }}
          onStatusChange={setJobStatus}
          onComplete={handleJobComplete}
          onError={handleJobError}
        />
      )}

      {isProcessing && (
        <JobStatus
          jobId={currentJobId}
          jobStatus={jobStatus}
          isProcessing={isProcessing}
          processDescription={`Mapping CLOs against predefined PLOs from ${selectedIds.length} comparison${selectedIds.length !== 1 ? 's' : ''}...`}
        />
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <span className="text-red-500 text-lg">⚠️</span>
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Result view */}
      {ploResult && !isProcessing && (
        <div className="space-y-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Selection
          </button>
          <PLOHeatmap
            program_learning_outcomes={ploResult.program_learning_outcomes || []}
            course_instances={ploResult.course_instances || []}
            summary={ploResult.summary}
          />
        </div>
      )}

      {/* Selection section */}
      {!ploResult && !isProcessing && (
        <>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex items-start justify-between mb-4 gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">PLO Mapping (Custom)</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Upload a file of predefined PLOs (DOCX, PDF, CSV, or XLSX) and select CLO comparisons to map against them.
                </p>
              </div>
              <button
                onClick={() => { if (!canRun || isProcessing) return; setSelectedIds(ids => [...ids].sort((a, b) => { const la = allSources.find(x => x.id === a)?.filenames?.join(', ') || a; const lb = allSources.find(x => x.id === b)?.filenames?.join(', ') || b; return la.localeCompare(lb) })); setShowOrderConfirm(true) }}
                disabled={!canRun || isProcessing}
                className="shrink-0 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center gap-2"
              >
                {isProcessing ? (
                  <><Loader2 className="animate-spin h-4 w-4" /> Running...</>
                ) : (
                  <>Map PLOs ({selectedIds.length} selected)</>
                )}
              </button>
            </div>

            {/* CSV Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Predefined PLO File <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Upload a <code className="bg-gray-100 px-1 rounded">.docx</code>, <code className="bg-gray-100 px-1 rounded">.pdf</code>, <code className="bg-gray-100 px-1 rounded">.csv</code>, or <code className="bg-gray-100 px-1 rounded">.xlsx</code> file containing your predefined PLOs — either as a table (id + statement columns) or as a numbered/bulleted list.
              </p>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors text-sm text-gray-700">
                  <FileText className="h-4 w-4 text-gray-500" />
                  {ploDocxFile ? ploDocxFile.name : 'Choose file (DOCX, PDF, CSV, XLSX)...'}
                  <input
                    type="file"
                    accept=".docx,.pdf,.csv,.xlsx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                {ploDocxFile && (
                  <button
                    onClick={() => { setPloDocxFile(null); setPloFileBase64('') }}
                    className="text-gray-400 hover:text-gray-600"
                    title="Remove file"
                  >
                    <X size={16} />
                  </button>
                )}
                {ploFileBase64 && (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3.5 w-3.5" /> File loaded
                  </span>
                )}
              </div>
            </div>

            {/* Source selection table */}
            {bulkLoLoading || docsLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
              </div>
            ) : allSources.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No sources found.</p>
                <p className="text-sm text-gray-500 mt-2">
                  Run a CLO comparison or a bulk LO analysis first.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-700 mb-2">Select Sources</p>
                {/* Select all toggle */}
                <label className="flex items-center gap-2 mb-3 w-fit text-sm text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    checked={sortedMapSources.length > 0 && selectedIds.length === sortedMapSources.length}
                    ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < sortedMapSources.length }}
                    onChange={() => setSelectedIds(
                      selectedIds.length === sortedMapSources.length ? [] : sortedMapSources.map(s => s.id)
                    )}
                  />
                  {sortedMapSources.length > 0 && selectedIds.length === sortedMapSources.length ? 'Deselect all' : `Select all (${sortedMapSources.length})`}
                </label>
                <PaginationTop total={sortedMapSources.length} page={mapSourcePage} pageSize={mapSourcePageSize} label="sources"
                  onPageSizeChange={n => { setMapSourcePageSize(n); setMapSourcePage(1) }} />
                <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 w-12" />
                        <th onClick={() => handleMapSort('type')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                          <div className="flex items-center gap-2">Type <MapSortIcon col="type" /></div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Folder</th>
                        <th onClick={() => handleMapSort('created')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                          <div className="flex items-center gap-2">Created <MapSortIcon col="created" /></div>
                        </th>
                        <th onClick={() => handleMapSort('documents')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                          <div className="flex items-center gap-2">Files <MapSortIcon col="documents" /></div>
                        </th>
                        <th onClick={() => handleMapSort('completed')} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                          <div className="flex items-center gap-2">Completed <MapSortIcon col="completed" /></div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {mapSourcePaged.map(a => {
                        const isSelected = selectedIds.includes(a.id)
                        return (
                          <tr
                            key={a.id}
                            onClick={() => toggleSelect(a.id)}
                            className={`transition-colors cursor-pointer ${
                              isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-4 py-3">
                              <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                                isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                              }`}>
                                {isSelected && (
                                  <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {a.sourceType === 'bulk-lo'
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Bulk LO</span>
                                : a.sourceType === 'bulk-analysis'
                                  ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700" title="Learning objectives reused from a main Analysis tab bulk run">Bulk Analysis History</span>
                                  : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">LO</span>
                              }
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {a.sourceType === 'lo' && a.folder ? <span style={{ textTransform: 'uppercase' }}>{a.folder}</span> : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{fmtDate(a.created_at)}</td>
                            <td className="px-4 py-3">
                              {a.filenames && a.filenames.length > 0 ? (
                                <ul className="text-xs text-gray-600 space-y-0.5">
                                  {a.filenames.map((f, i) => (
                                    <li key={i} className="truncate max-w-xs" title={f}>{f}</li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-sm text-gray-900">{a.document_count} files</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{fmtDate(a.completed_at)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{a.created_by || '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <PaginationBottom total={sortedMapSources.length} page={mapSourcePage} pageSize={mapSourcePageSize}
                  onPrev={() => setMapSourcePage(p => Math.max(1, p - 1))}
                  onNext={() => setMapSourcePage(p => Math.min(Math.ceil(sortedMapSources.length / mapSourcePageSize), p + 1))} />
              </>
            )}

          </div>

          {/* Order confirmation modal */}
          {showOrderConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Confirm Column Order</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    This will be the left-to-right column order in the heatmap. Drag to reorder before generating.
                  </p>
                </div>
                <DndContext sensors={mapSensors} collisionDetection={closestCenter} onDragEnd={handleMapDragEnd}>
                  <SortableContext items={selectedIds} strategy={verticalListSortingStrategy}>
                    <ol className="space-y-2 max-h-[60vh] overflow-y-auto">
                      {selectedIds.map((id, idx) => {
                        const src = allSources.find(x => x.id === id)
                        const def = src?.filenames?.join(', ') || id.slice(0, 8)
                        return (
                          <SortableSourceItem
                            key={id}
                            id={id}
                            label={labelOverrides[id] ?? def}
                            idx={idx}
                            onRename={v => setLabelOverrides(o => ({ ...o, [id]: v }))}
                          />
                        )
                      })}
                    </ol>
                  </SortableContext>
                </DndContext>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setShowOrderConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => { setShowOrderConfirm(false); handleRunPLOMapping() }}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Confirm &amp; Generate
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Past PLO mappings */}
          {!pastPloLoading && pastPloMappings.length > 0 && (
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Past PLO Mappings</h2>
              <PaginationTop total={pastPloMappings.length} page={mapPastPage} pageSize={mapPastPageSize} label="mappings"
                onPageSizeChange={n => { setMapPastPageSize(n); setMapPastPage(1) }} />
              <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comparisons Used</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mapPastPaged.map(a => (
                      <tr key={a.analysis_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{fmtDate(a.created_at)}</td>
                        <td className="px-4 py-3">
                          {a.filenames && a.filenames.length > 0 ? (
                            <ul className="text-xs text-gray-600 space-y-0.5">
                              {a.filenames.map((f, i) => (
                                <li key={i} className="truncate max-w-xs" title={f}>{f}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-sm text-gray-900">{a.document_count} comparison(s)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {a.llm_cost ? `$${parseFloat(a.llm_cost).toFixed(4)}` : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{a.created_by || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap flex items-center gap-1">
                          <button
                            onClick={() => handleViewPast(a)}
                            disabled={viewLoading && viewingPloId === a.analysis_id}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                            title="View PLO heatmap"
                          >
                            {viewLoading && viewingPloId === a.analysis_id
                              ? <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                              : <Eye className="h-4 w-4" />
                            }
                          </button>
                          <button
                            onClick={() => handleDownloadPast(a)}
                            disabled={downloadingId === a.analysis_id}
                            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                            title="Download PDF"
                          >
                            {downloadingId === a.analysis_id
                              ? <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full" />
                              : <Download className="h-4 w-4" />
                            }
                          </button>
                          <button
                            onClick={() => handleDeletePast(a.analysis_id)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBottom total={pastPloMappings.length} page={mapPastPage} pageSize={mapPastPageSize}
                onPrev={() => setMapPastPage(p => Math.max(1, p - 1))}
                onNext={() => setMapPastPage(p => Math.min(Math.ceil(pastPloMappings.length / mapPastPageSize), p + 1))} />
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setAnalysisToDelete(null) }}
        onConfirm={confirmDeletePast}
        title="Delete Analysis"
        message="Are you sure you want to delete this analysis? This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  )
}
